{
  "$version": 8,
  "$root": {
    "version": {
      "required": true,
      "type": "enum",
      "values": [
        8
      ],
      "doc": "Style specification version number. Must be 8.",
      "example": 8
    },
    "fragment": {
      "type": "boolean",
      "doc": "Indicates that a style is a fragment style.",
      "example": true
    },
    "name": {
      "type": "string",
      "doc": "A human-readable name for the style.",
      "example": "Bright"
    },
    "metadata": {
      "type": "*",
      "doc": "Arbitrary properties useful to track with the stylesheet, but do not influence rendering. Properties should be prefixed to avoid collisions, like 'mapbox:'."
    },
    "center": {
      "type": "array",
      "value": "number",
      "doc": "Default map center in longitude and latitude. The style center will be used only if the map has not been positioned by other means (e.g. map options or user interaction).",
      "example": [
        -73.9749,
        40.7736
      ]
    },
    "zoom": {
      "type": "number",
      "doc": "Default zoom level. The style zoom will be used only if the map has not been positioned by other means (e.g. map options or user interaction).",
      "example": 12.5
    },
    "bearing": {
      "type": "number",
      "default": 0,
      "period": 360,
      "units": "degrees",
      "doc": "Default bearing, in degrees. The bearing is the compass direction that is \"up\"; for example, a bearing of 90° orients the map so that east is up. This value will be used only if the map has not been positioned by other means (e.g. map options or user interaction).",
      "example": 29
    },
    "pitch": {
      "type": "number",
      "default": 0,
      "units": "degrees",
      "doc": "Default pitch, in degrees. Zero is perpendicular to the surface, for a look straight down at the map, while a greater value like 60 looks ahead towards the horizon. The style pitch will be used only if the map has not been positioned by other means (e.g. map options or user interaction).",
      "example": 50
    },
    "light": {
      "type": "light",
      "doc": "The global light source. Note: This API is deprecated. Prefer using `flat` light type instead in the `lights` API.",
      "example": {
        "anchor": "viewport",
        "color": "white",
        "intensity": 0.4
      }
    },
    "lights": {
      "required": false,
      "type": "array",
      "value": "light-3d",
      "doc": "Array of light sources affecting the whole map and the default for 3D style, mutually exclusive with the light property",
      "example": [
        {
          "id": "environment",
          "type": "ambient",
          "properties": {
            "color": "rgba(255.0, 0.0, 0.0, 1.0)",
            "intensity": 0.4
          }
        },
        {
          "id": "sun_light",
          "type": "directional",
          "properties": {
            "color": "rgba(255.0, 0.0, 0.0, 1.0)",
            "intensity": 0.4,
            "direction": [200.0, 40.0],
            "cast-shadows": true,
            "shadow-intensity": 0.2
          }
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "terrain": {
      "type": "terrain",
      "optional": true,
      "doc": "A global modifier that elevates layers and markers based on a DEM data source."
    },
    "fog": {
      "type": "fog",
      "doc": "A global effect that fades layers and markers based on their distance to the camera. The fog can be used to approximate the effect of atmosphere on distant objects and enhance the depth perception of the map when used with terrain or 3D features. Note: fog is renamed to atmosphere in the Android and iOS SDKs and planned to be changed in GL-JS v.3.0.0."
    },
    "snow": {
      "type": "snow",
      "doc": "Global precipitation particle-based snow. Having snow present in the style forces constant map repaint mode",
      "experimental": true
    },
    "rain": {
      "type": "rain",
      "doc": "Global precipitation particle-based rain effect. Having rain present in the style forces constant map repaint mode.",
      "experimental": true
    },
    "camera": {
      "type": "camera",
      "doc": "Global setting to control additional camera intrinsics parameters, e.g. projection type (perspective / orthographic)."
    },
    "color-theme": {
      "type": "colorTheme",
      "doc": "A global modifier for the colors of the style."
    },
    "indoor": {
      "type": "indoor",
      "private": true,
      "experimental": true,
      "doc": "Controls the behaviour of indoor features."
    },
    "imports": {
      "type": "array",
      "value": "import",
      "doc": "Imports other styles into this style.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "iconsets": {
      "experimental": true,
      "type": "iconsets",
      "doc": "A collection of icon sets",
      "sdk-support": {
        "basic functionality": {
          "js": "3.11.0",
          "android": "11.11.0",
          "ios": "11.11.0"
        }
      }
    },
    "schema": {
      "type": "schema",
      "doc": "Definition of the schema for configuration options.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "sources": {
      "required": true,
      "type": "sources",
      "doc": "Data source specifications.",
      "example": {
        "mapbox-streets": {
          "type": "vector",
          "url": "mapbox://mapbox.mapbox-streets-v6"
        }
      }
    },
    "sprite": {
      "type": "string",
      "doc": "A base URL for retrieving the sprite image and metadata. The extensions `.png`, `.json` and scale factor `@2x.png` will be automatically appended. This property is required if any layer uses the `background-pattern`, `fill-pattern`, `line-pattern`, `fill-extrusion-pattern`, or `icon-image` properties. The URL must be absolute, containing the [scheme, authority and path components](https://en.wikipedia.org/wiki/URL#Syntax).",
      "example": "mapbox://sprites/mapbox/bright-v8"
    },
    "glyphs": {
      "type": "string",
      "doc": "A URL template for loading signed-distance-field glyph sets in PBF format. The URL must include `{fontstack}` and `{range}` tokens. This property is required if any layer uses the `text-field` layout property. The URL must be absolute, containing the [scheme, authority and path components](https://en.wikipedia.org/wiki/URL#Syntax).",
      "default": "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
      "example": "mapbox://fonts/mapbox/{fontstack}/{range}.pbf"
    },
    "transition": {
      "type": "transition",
      "doc": "A global transition definition to use as a default across properties, to be used for timing transitions between one value and the next when no property-specific transition is set. Collision-based symbol fading is controlled independently of the style's `transition` property.",
      "example": {
        "duration": 300,
        "delay": 0
      }
    },
    "projection": {
      "type": "projection",
      "doc": "The projection the map should be rendered in. Supported projections are Mercator, Globe, Albers, Equal Earth, Equirectangular (WGS84), Lambert conformal conic, Natural Earth, and Winkel Tripel. Terrain, sky and fog are supported by only Mercator and globe. CustomLayerInterface is not supported outside of Mercator.",
      "example": {
        "name": "albers",
        "center": [-154, 50],
        "parallels": [55, 65]
      }
    },
    "layers": {
      "required": true,
      "type": "array",
      "value": "layer",
      "doc": "Layers will be drawn in the order of this array.",
      "example": [
        {
          "id": "water",
          "source": "mapbox-streets",
          "source-layer": "water",
          "type": "fill",
          "paint": {
            "fill-color": "#00ffff"
          }
        }
      ]
    },
    "models": {
      "type": "models",
      "doc": "Specification of models used in the style.",
      "example": {
        "spruce1-lod0": "asset://spruce1-lod0.glb",
        "spruce1-lod1": "asset://spruce1-lod1.glb",
        "spruce1-lod2": "asset://spruce1-lod2.glb"
      }
    },
   "featuresets": {
      "experimental": true,
      "type": "featuresets",
      "doc": "Defines sets of features for querying, interaction, and state management on the map, referencing individual layers or subsets of layers within the map's style.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.9.0",
          "android": "11.9.0",
          "ios": "11.9.0"
        }
      },
      "example": {
        "poi": {
          "selectors": [
            {
              "layer": "poi",
              "properties": {
                "type": ["get", "type"],
                "name": ["get", "name"],
                "brand": "ABC"
              }
            }
          ]
        }
      }
    }
  },
  "featuresets": {
    "experimental": true,
    "*": {
      "type": "featureset",
      "doc": "Defines a combined set of features from one or more underlying layers within the current style. Features in a style featureset can be queried, interacted with, and their states can be queried and updated."
    }
  },
  "featureset": {
    "experimental": true,
    "metadata": {
      "experimental": true,
      "type": "*",
      "doc": "Arbitrary properties useful to track with the stylesheet, but do not influence rendering. Properties should be prefixed to avoid collisions, like 'mapbox:'."
    },
    "selectors": {
      "experimental": true,
      "type": "array",
      "value": "selector",
      "doc": "A collection of categorized selectors."
    }
  },
  "selector": {
    "experimental": true,
    "layer": {
      "experimental": true,
      "type": "string",
      "doc": "The ID of a layer that exists in the current style.",
      "required": true
    },
    "properties": {
      "experimental": true,
      "type": "selectorProperty",
      "required": false,
      "doc": "Properties accessible to the end user through queried feautures. If properties are empty, no feature properties are exposed. If undefined, all original feature properties will be accessible."
    },
    "featureNamespace": {
      "experimental": true,
      "type": "string",
      "required": false,
      "doc": "An optional field that represents the feature namespace defined by the selector within a featureset to which this feature belongs. If the underlying source is the same for multiple selectors within a featureset, the same featureNamespace should be used across those selectors."
    },
    "_uniqueFeatureID": {
      "experimental": true,
      "type": "boolean",
      "private": true,
      "required": false,
      "doc": "Internal flag used for standard style, in case multiple features sharing the same featureId, only one feature will be returned for feature query."
    }
  },
  "selectorProperty": {
    "experimental": true,
    "*": {
      "experimental": true,
      "type": "*",
      "doc": "The value of the property. It can be an expression that generates the returned value from the feature, or a constant value specifying the returned value."
    }
  },
  "model": {
    "type": "string",
    "doc": "A URL to a model resource. Supported protocols are `http:`, `https:`, and `mapbox://<Model ID>`.",
    "required": true
  },
  "import": {
    "id": {
      "type": "string",
      "doc": "Unique import name.",
      "required": true
    },
    "url": {
      "type": "string",
      "doc": "The URL of the style.",
      "required": true
    },
    "config": {
      "type": "config",
      "doc": "Configuration values for the imported style's options."
    },
    "data": {
      "type": "$root",
      "doc": "The inlined style that must correspond to the contents of the specified URL."
    },
    "color-theme": {
      "type": "colorTheme",
      "optional": true,
      "doc": "If specified, it overrides the color-theme of the imported style."
    }
  },
  "config": {
    "*": {
      "type": "*",
      "doc": "Value of the imported style's configuration option."
    }
  },
  "schema": {
    "*": {
      "type": "option",
      "doc": "Specification of a configuration option."
    }
  },
  "option": {
    "default": {
      "type": "*",
      "doc": "Default configuration value for this option.",
      "property-type": "data-constant",
      "expression": {
        "interpolated": false
      },
      "required": true
    },
    "type": {
      "type": "enum",
      "doc": "The type this value is coerced to after evaluating the expression. If unspecified, the result is returned as is and is not validated.",
      "values": {
        "string": {
          "doc": "The result will be coerced to a string."
        },
        "number": {
          "doc": "The result will be coerced to a number."
        },
        "boolean": {
          "doc": "The result will be coerced to a boolean."
        },
        "color": {
          "doc": "The result will be coerced to a color."
        },
        "object": {
          "doc": "The result will be coerced to an object.",
          "sdk-support": {
            "basic functionality": {
              "js": "3.29.0",
              "android": "11.29.0",
              "ios": "11.29.0"
            }
          }
        }
      }
    },
    "array": {
      "type": "boolean",
      "doc": "If true, this option is returned as an array"
    },
    "minValue": {
      "type": "number",
      "doc": "If this option is a number, this specifies the minimum allowed value. Values lower than this will be clamped to the minimum value."
    },
    "maxValue": {
      "type": "number",
      "doc": "If this option is a number, this specifies the maximum allowed value. Values higher than this will be clamped to the maximum value."
    },
    "stepValue": {
      "type": "number",
      "doc": "If this option is a number, this specifies the increment between allowed values. Values will be rounded towards the nearest allowed value."
    },
    "values": {
      "type": "array",
      "value": "*",
      "doc": "If this option is specified, the result must be one of the given values. Otherwise, the default value is used instead."
    },
    "metadata": {
      "type": "*",
      "doc": "Arbitrary properties useful to track with the layer, but do not influence rendering. Properties should be prefixed to avoid collisions, like 'mapbox:'."
    }
  },
  "models" : {
    "*": {
      "type": "model",
      "doc": "A URL to a model resource. Supported protocols are `http:`, `https:`, and `mapbox://<Model ID>`."
    }
  },
  "light-3d": {
    "id": {
      "type": "string",
      "doc": "Unique light name.",
      "required": true
    },
    "properties": {
      "type": "properties",
      "doc": "Properties of the light."
    },
    "type": {
      "type": "enum",
      "doc": "Type of the light to be added",
      "values": {
        "ambient": {
          "doc": "An indirect light affecting all objects in the map adding a constant amount of light on them. It has no explicit direction and cannot cast shadows.",
          "sdk-support": {
            "basic functionality": {
              "js": "3.0.0",
              "android": "11.0.0",
              "ios": "11.0.0"
            }
          }
        },
        "directional": {
          "doc": "A light that has a direction and is located at infinite distance, so its rays are parallel. It simulates the sun light and can cast shadows.",
          "sdk-support": {
            "basic functionality": {
              "js": "3.0.0",
              "android": "11.0.0",
              "ios": "11.0.0"
            }
          }
        },
        "flat": {
          "doc": "A global directional light source which is only applied on 3D and hillshade layers. Using this type disables other light sources.",
          "sdk-support": {
            "basic functionality": {
              "js": "3.0.0",
              "android": "11.0.0",
              "ios": "11.0.0"
            }
          }
        }
      }
    }
  },
  "properties": [
    "properties_light_directional",
    "properties_light_ambient",
    "properties_light_flat"
  ],
  "properties_light_directional": {
    "direction": {
      "type": "array",
      "default": [210, 30],
      "minimum": [0, 0],
      "maximum": [360, 90],
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "doc": "Direction of the light source specified as [a azimuthal angle, p polar angle] where a indicates the azimuthal angle of the light relative to north (in degrees and proceeding clockwise), and p indicates polar angle of the light (from 0°, directly above, to 180°, directly below).",
      "example": [90, 40],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "color": {
      "type": "color",
      "property-type": "data-constant",
      "default": "#ffffff",
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "use-theme": true,
      "transition": true,
      "doc": "Color of the directional light.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "intensity": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.5,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "A multiplier for the color of the directional light.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "cast-shadows": {
      "type": "boolean",
      "default": false,
      "doc": "Enable/Disable shadow casting for this light",
      "transition": false,
      "property-type": "data-constant",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "shadow-quality": {
      "type": "number",
      "property-type": "data-constant",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "transition": false,
      "doc": "Determines the quality of the shadows on the map. A value of 1 ensures the highest quality and is the default value.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0"
        }
      },
      "experimental": true
    },
    "shadow-intensity": {
      "type": "number",
      "property-type": "data-constant",
      "default": 1.0,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "doc": "Determines the shadow strength, affecting the shadow receiver surfaces final color. Values near 0.0 reduce the shadow contribution to the final color. Values near to 1.0 make occluded surfaces receive almost no directional light. Designed to be used mostly for transitioning between values 0 and 1.",
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "shadow-draw-before-layer": {
      "type": "string",
      "property-type": "data-constant",
      "doc": "Specify a layer before which shadows are drawn on the ground. If not specified, shadows are drawn after the last 3D layer. This property does not affect shadows on terrain.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.18.0",
          "android": "11.18.0",
          "ios": "11.18.0"
        }
      },
      "experimental": true
    }
  },
  "properties_light_ambient": {
    "color": {
      "type": "color",
      "property-type": "data-constant",
      "default": "#ffffff",
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "use-theme": true,
      "transition": true,
      "doc": "Color of the ambient light.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "intensity": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.5,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "A multiplier for the color of the ambient light.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    }
  },
  "properties_light_flat": {
    "anchor": {
      "type": "enum",
      "default": "viewport",
      "values": {
        "map": {
          "doc": "The position of the light source is aligned to the rotation of the map."
        },
        "viewport": {
          "doc": "The position of the light source is aligned to the rotation of the viewport. If terrain is enabled, performance regressions may occur in certain scenarios, particularly on lower-end hardware. Ensure that you test your target scenarios on the appropriate hardware to verify performance."
        }
      },
      "property-type": "data-constant",
      "transition": false,
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "doc": "Whether extruded geometries are lit relative to the map or viewport.",
      "example": "map",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "position": {
      "type": "array",
      "default": [
        1.15,
        210,
        30
      ],
      "length": 3,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "doc": "Position of the light source relative to lit (extruded) geometries, in [r radial coordinate, a azimuthal angle, p polar angle] where r indicates the distance from the center of the base of an object to its light, a indicates the position of the light relative to 0° (0° when `light.anchor` is set to `viewport` corresponds to the top of the viewport, or 0° when `light.anchor` is set to `map` corresponds to due north, and degrees proceed clockwise), and p indicates the height of the light (from 0°, directly above, to 180°, directly below).",
      "example": [
        1.5,
        90,
        80
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "color": {
      "type": "color",
      "property-type": "data-constant",
      "default": "#ffffff",
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "use-theme": true,
      "transition": true,
      "doc": "Color tint for lighting extruded geometries.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "intensity": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.5,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "Intensity of lighting (on a scale from 0 to 1). Higher numbers will present as more extreme contrast.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    }
  },
  "iconsets": {
    "*": {
      "type": "iconset",
      "doc": "Specification of an icon set. For sprite icon set, a URL must be provided. For `raster-array` source icon set, a source name must be provided."
    }
  },
  "iconset": [
    "iconset_sprite",
    "iconset_source"
  ],
  "iconset_sprite": {
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "sprite": {
          "doc": "A sprite icon set."
        }
      },
      "doc": "The type of the icon set."
    },
    "url": {
      "required": true,
      "type": "string",
      "doc": "A base URL for retrieving the icon set and metadata. The extension `.pbf` will be automatically appended. The URL must be absolute, containing the [scheme, authority and path components](https://en.wikipedia.org/wiki/URL#Syntax).",
      "example": "mapbox://sprites/mapbox/bright"
    }
  },
  "iconset_source": {
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "source": {
          "doc": "A source icon set."
        }
      },
      "doc": "The type of the icon set."
    },
    "source": {
      "required": true,
      "type": "string",
      "doc": "Name of a source of `raster-array` type to be used for the icon set."
    }
  },
  "sources": {
    "*": {
      "type": "source",
      "doc": "Specification of a data source. For vector and raster sources, either TileJSON or a URL to a TileJSON must be provided. For image and video sources, a URL must be provided. For GeoJSON sources, a URL or inline GeoJSON must be provided."
    }
  },
  "source": [
    "source_vector",
    "source_raster",
    "source_raster_dem",
    "source_raster_array",
    "source_geojson",
    "source_video",
    "source_image",
    "source_model"
  ],
  "source_vector": {
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "vector": {
          "doc": "A vector tile source."
        }
      },
      "doc": "The type of the source."
    },
    "url": {
      "type": "string",
      "doc": "A URL to a TileJSON resource. Supported protocols are `http:`, `https:`, and `mapbox://<Tileset ID>`. Required if `tiles` is not provided."
    },
    "tiles": {
      "type": "array",
      "value": "string",
      "doc": "An array of one or more tile source URLs, as in the TileJSON spec. Required if `url` is not provided."
    },
    "bounds": {
      "type": "array",
      "value": "number",
      "length": 4,
      "default": [
        -180,
        -85.051129,
        180,
        85.051129
      ],
      "doc": "An array containing the longitude and latitude of the southwest and northeast corners of the source's bounding box in the following order: `[sw.lng, sw.lat, ne.lng, ne.lat]`. When this property is included in a source, no tiles outside of the given bounds are requested by Mapbox GL."
    },
    "extra_bounds": {
      "type": "array",
      "value": {
        "type": "array",
        "value": "number",
        "length": 4,
        "doc": "An array containing the longitude and latitude of the southwest and northeast corners of the source's bounding box in the following order: `[sw.lng, sw.lat, ne.lng, ne.lat]`."
      },
      "doc": "An array of additional discrete geographic regions where tiles are available. When used alongside the `bounds` property, these regions act as an additional filter - Mapbox GL will only request tiles that are both within the `bounds` and any of the regions defined in `extra_bounds`. When used independently (without `bounds`), Mapbox GL will request tiles that fall within any of the regions in `extra_bounds`. This allows for more fine-grained control over tile requests, particularly when dealing with sparse data coverage."
    },
    "scheme": {
      "type": "enum",
      "values": {
        "xyz": {
          "doc": "Slippy map tilenames scheme."
        },
        "tms": {
          "doc": "OSGeo spec scheme."
        }
      },
      "default": "xyz",
      "doc": "Influences the y direction of the tile coordinates. The global-mercator (aka Spherical Mercator) profile is assumed."
    },
    "minzoom": {
      "type": "number",
      "default": 0,
      "doc": "Minimum zoom level for which tiles are available, as in the TileJSON spec."
    },
    "maxzoom": {
      "type": "number",
      "default": 22,
      "doc": "Maximum zoom level for which tiles are available, as in the TileJSON spec. Data from tiles at the maxzoom are used when displaying the map at higher zoom levels."
    },
    "attribution": {
      "type": "string",
      "doc": "Contains an attribution to be displayed when the map is shown to a user."
    },
    "promoteId": {
      "type": "promoteId",
      "doc": "A property to use as a feature id (for feature state). It can be a property name, or an expression to evaluate as the ID, or an object of the form `{<sourceLayer>: <propertyName/expression>}`. The expression can only be feature dependent if it is used. If specified as a string for a vector tile source, the same property is used across all its source layers. If specified as an object only specified source layers will have id overriden, others will fallback to original feature id"
    },
    "volatile": {
      "type": "boolean",
      "default": false,
      "doc": "A setting to determine whether a source's tiles are cached locally.",
      "sdk-support": {
        "basic functionality": {
          "android": "9.3.0",
          "ios": "5.10.0"
        }
      }
    },
    "*": {
      "type": "*",
      "doc": "Other keys to configure the data source."
    }
  },
  "source_raster": {
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "raster": {
          "doc": "A raster tile source."
        }
      },
      "doc": "The type of the source."
    },
    "url": {
      "type": "string",
      "doc": "A URL to a TileJSON resource. Supported protocols are `http:`, `https:`, and `mapbox://<Tileset ID>`. Required if `tiles` is not provided."
    },
    "tiles": {
      "type": "array",
      "value": "string",
      "doc": "An array of one or more tile source URLs, as in the TileJSON spec. Required if `url` is not provided."
    },
    "bounds": {
      "type": "array",
      "value": "number",
      "length": 4,
      "default": [
        -180,
        -85.051129,
        180,
        85.051129
      ],
      "doc": "An array containing the longitude and latitude of the southwest and northeast corners of the source's bounding box in the following order: `[sw.lng, sw.lat, ne.lng, ne.lat]`. When this property is included in a source, no tiles outside of the given bounds are requested by Mapbox GL."
    },
    "extra_bounds": {
      "type": "array",
      "value": {
        "type": "array",
        "value": "number",
        "length": 4,
        "doc": "An array containing the longitude and latitude of the southwest and northeast corners of the source's bounding box in the following order: `[sw.lng, sw.lat, ne.lng, ne.lat]`."
      },
      "doc": "An array of additional discrete geographic regions where tiles are available. When used alongside the `bounds` property, these regions act as an additional filter - Mapbox GL will only request tiles that are both within the `bounds` and any of the regions defined in `extra_bounds`. When used independently (without `bounds`), Mapbox GL will request tiles that fall within any of the regions in `extra_bounds`. This allows for more fine-grained control over tile requests, particularly when dealing with sparse data coverage."
    },
    "minzoom": {
      "type": "number",
      "default": 0,
      "doc": "Minimum zoom level for which tiles are available, as in the TileJSON spec."
    },
    "maxzoom": {
      "type": "number",
      "default": 22,
      "doc": "Maximum zoom level for which tiles are available, as in the TileJSON spec. Data from tiles at the maxzoom are used when displaying the map at higher zoom levels."
    },
    "tileSize": {
      "type": "number",
      "default": 512,
      "units": "pixels",
      "doc": "The minimum visual size to display tiles for this layer. Only configurable for raster layers."
    },
    "scheme": {
      "type": "enum",
      "values": {
        "xyz": {
          "doc": "Slippy map tilenames scheme."
        },
        "tms": {
          "doc": "OSGeo spec scheme."
        }
      },
      "default": "xyz",
      "doc": "Influences the y direction of the tile coordinates. The global-mercator (aka Spherical Mercator) profile is assumed."
    },
    "attribution": {
      "type": "string",
      "doc": "Contains an attribution to be displayed when the map is shown to a user."
    },
    "volatile": {
      "type": "boolean",
      "default": false,
      "doc": "A setting to determine whether a source's tiles are cached locally.",
      "sdk-support": {
        "basic functionality": {
          "android": "9.3.0",
          "ios": "5.10.0"
        }
      }
    },
    "*": {
      "type": "*",
      "doc": "Other keys to configure the data source."
    }
  },
  "source_raster_dem": {
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "raster-dem": {
          "doc": "A RGB-encoded raster DEM source"
        }
      },
      "doc": "The type of the source."
    },
    "url": {
      "type": "string",
      "doc": "A URL to a TileJSON resource. Supported protocols are `http:`, `https:`, and `mapbox://<Tileset ID>`. Required if `tiles` is not provided."
    },
    "tiles": {
      "type": "array",
      "value": "string",
      "doc": "An array of one or more tile source URLs, as in the TileJSON spec. Required if `url` is not provided."
    },
    "bounds": {
      "type": "array",
      "value": "number",
      "length": 4,
      "default": [
        -180,
        -85.051129,
        180,
        85.051129
      ],
      "doc": "An array containing the longitude and latitude of the southwest and northeast corners of the source's bounding box in the following order: `[sw.lng, sw.lat, ne.lng, ne.lat]`. When this property is included in a source, no tiles outside of the given bounds are requested by Mapbox GL."
    },
    "extra_bounds": {
      "type": "array",
      "value": {
        "type": "array",
        "value": "number",
        "length": 4,
        "doc": "An array containing the longitude and latitude of the southwest and northeast corners of the source's bounding box in the following order: `[sw.lng, sw.lat, ne.lng, ne.lat]`."
      },
      "doc": "An array of additional discrete geographic regions where tiles are available. When used alongside the `bounds` property, these regions act as an additional filter - Mapbox GL will only request tiles that are both within the `bounds` and any of the regions defined in `extra_bounds`. When used independently (without `bounds`), Mapbox GL will request tiles that fall within any of the regions in `extra_bounds`. This allows for more fine-grained control over tile requests, particularly when dealing with sparse data coverage."
    },
    "minzoom": {
      "type": "number",
      "default": 0,
      "doc": "Minimum zoom level for which tiles are available, as in the TileJSON spec."
    },
    "maxzoom": {
      "type": "number",
      "default": 22,
      "doc": "Maximum zoom level for which tiles are available, as in the TileJSON spec. Data from tiles at the maxzoom are used when displaying the map at higher zoom levels."
    },
    "tileSize": {
      "type": "number",
      "default": 512,
      "units": "pixels",
      "doc": "The minimum visual size to display tiles for this layer. Only configurable for raster layers."
    },
    "attribution": {
      "type": "string",
      "doc": "Contains an attribution to be displayed when the map is shown to a user."
    },
    "encoding": {
      "type": "enum",
      "values": {
        "terrarium": {
          "doc": "Terrarium format PNG tiles. See https://aws.amazon.com/es/public-datasets/terrain/ for more info."
        },
        "mapbox": {
          "doc": "Mapbox Terrain RGB tiles. See https://www.mapbox.com/help/access-elevation-data/#mapbox-terrain-rgb for more info."
        }
      },
      "default": "mapbox",
      "doc": "The encoding used by this source. Mapbox Terrain RGB is used by default"
    },
    "volatile": {
      "type": "boolean",
      "default": false,
      "doc": "A setting to determine whether a source's tiles are cached locally.",
      "sdk-support": {
        "basic functionality": {
          "android": "9.3.0",
          "ios": "5.10.0"
        }
      }
    },
    "*": {
      "type": "*",
      "doc": "Other keys to configure the data source."
    }
  },
  "source_raster_array": {
    "experimental": true,
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "raster-array": {
          "doc": "A raster array source"
        }
      },
      "doc": "The type of the source."
    },
    "url": {
      "type": "string",
      "doc": "A URL to a TileJSON resource. Supported protocols are `http:`, `https:`, and `mapbox://<Tileset ID>`. Required if `tiles` is not provided."
    },
    "tiles": {
      "type": "array",
      "value": "string",
      "doc": "An array of one or more tile source URLs, as in the TileJSON spec. Required if `url` is not provided."
    },
    "bounds": {
      "type": "array",
      "value": "number",
      "length": 4,
      "default": [
        -180,
        -85.051129,
        180,
        85.051129
      ],
      "doc": "An array containing the longitude and latitude of the southwest and northeast corners of the source's bounding box in the following order: `[sw.lng, sw.lat, ne.lng, ne.lat]`. When this property is included in a source, no tiles outside of the given bounds are requested by Mapbox GL."
    },
    "extra_bounds": {
      "type": "array",
      "value": {
        "type": "array",
        "value": "number",
        "length": 4,
        "doc": "An array containing the longitude and latitude of the southwest and northeast corners of the source's bounding box in the following order: `[sw.lng, sw.lat, ne.lng, ne.lat]`."
      },
      "doc": "An array of additional discrete geographic regions where tiles are available. When used alongside the `bounds` property, these regions act as an additional filter - Mapbox GL will only request tiles that are both within the `bounds` and any of the regions defined in `extra_bounds`. When used independently (without `bounds`), Mapbox GL will request tiles that fall within any of the regions in `extra_bounds`. This allows for more fine-grained control over tile requests, particularly when dealing with sparse data coverage."
    },
    "minzoom": {
      "type": "number",
      "default": 0,
      "doc": "Minimum zoom level for which tiles are available, as in the TileJSON spec."
    },
    "maxzoom": {
      "type": "number",
      "default": 22,
      "doc": "Maximum zoom level for which tiles are available, as in the TileJSON spec. Data from tiles at the maxzoom are used when displaying the map at higher zoom levels."
    },
    "tileSize": {
      "type": "number",
      "default": 512,
      "units": "pixels",
      "doc": "The minimum visual size to display tiles for this layer. Only configurable for raster layers."
    },
    "attribution": {
      "type": "string",
      "doc": "Contains an attribution to be displayed when the map is shown to a user."
    },
    "rasterLayers": {
      "type": "*",
      "doc": "Contains the description of the raster data layers and the bands contained within the tiles."
    },
    "volatile": {
      "type": "boolean",
      "default": false,
      "doc": "A setting to determine whether a source's tiles are cached locally.",
      "sdk-support": {
        "basic functionality": {
          "android": "9.3.0",
          "ios": "5.10.0"
        }
      }
    },
    "*": {
      "type": "*",
      "doc": "Other keys to configure the data source."
    }
  },
  "source_geojson": {
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "geojson": {
          "doc": "A GeoJSON data source."
        }
      },
      "doc": "The data type of the GeoJSON source."
    },
    "data": {
      "type": "*",
      "doc": "A URL to a GeoJSON file, or inline GeoJSON."
    },
    "maxzoom": {
      "type": "number",
      "default": 18,
      "doc": "Maximum zoom level at which to create vector tiles (higher means greater detail at high zoom levels)."
    },
    "minzoom": {
      "type": "number",
      "default": 0,
      "doc": "Minimum zoom level at which to create vector tiles"
    },
    "attribution": {
      "type": "string",
      "doc": "Contains an attribution to be displayed when the map is shown to a user."
    },
    "buffer": {
      "type": "number",
      "default": 128,
      "maximum": 512,
      "minimum": 0,
      "doc": "Size of the tile buffer on each side. A value of 0 produces no buffer. A value of 512 produces a buffer as wide as the tile itself. Larger values produce fewer rendering artifacts near tile edges and slower performance."
    },
    "filter": {
      "type": "*",
      "doc": "An expression for filtering features prior to processing them for rendering."
    },
    "tolerance": {
      "type": "number",
      "default": 0.375,
      "doc": "Douglas-Peucker simplification tolerance (higher means simpler geometries and faster performance)."
    },
    "cluster": {
      "type": "boolean",
      "default": false,
      "doc": "If the data is a collection of point features, setting this to true clusters the points by radius into groups. Cluster groups become new `Point` features in the source with additional properties:\n * `cluster` Is `true` if the point is a cluster \n * `cluster_id` A unqiue id for the cluster to be used in conjunction with the [cluster inspection methods](https://www.mapbox.com/mapbox-gl-js/api/#geojsonsource#getclusterexpansionzoom)\n * `point_count` Number of original points grouped into this cluster\n * `point_count_abbreviated` An abbreviated point count"
    },
    "clusterRadius": {
      "type": "number",
      "default": 50,
      "minimum": 0,
      "doc": "Radius of each cluster if clustering is enabled. A value of 512 indicates a radius equal to the width of a tile."
    },
    "clusterMaxZoom": {
      "type": "number",
      "doc": "Max zoom on which to cluster points if clustering is enabled. Defaults to one zoom less than maxzoom (so that last zoom features are not clustered). Clusters are re-evaluated at integer zoom levels so setting clusterMaxZoom to 14 means the clusters will be displayed until z15."
    },
    "clusterMinPoints": {
      "type": "number",
      "doc": "Minimum number of points necessary to form a cluster if clustering is enabled. Defaults to `2`."
    },
    "clusterProperties": {
      "type": "*",
      "doc": "An object defining custom properties on the generated clusters if clustering is enabled, aggregating values from clustered points. Has the form `{\"property_name\": [operator, map_expression]}`. `operator` is any expression function that accepts at least 2 operands (e.g. `\"+\"` or `\"max\"`) — it accumulates the property value from clusters/points the cluster contains; `map_expression` produces the value of a single point.\n\nExample: `{\"sum\": [\"+\", [\"get\", \"scalerank\"]]}`.\n\nFor more advanced use cases, in place of `operator`, you can use a custom reduce expression that references a special `[\"accumulated\"]` value, e.g.:\n`{\"sum\": [[\"+\", [\"accumulated\"], [\"get\", \"sum\"]], [\"get\", \"scalerank\"]]}`"
    },
    "lineMetrics": {
      "type": "boolean",
      "default": false,
      "doc": "Whether to calculate line distance metrics. This is required for line layers that specify `line-gradient` values."
    },
    "generateId": {
      "type": "boolean",
      "default": false,
      "doc": "Whether to generate ids for the GeoJSON features. When enabled, the `feature.id` property will be auto assigned based on its index in the `features` array, over-writing any previous values."
    },
    "promoteId": {
      "type": "promoteId",
      "doc": "A property to use as a feature id (for feature state). Either a property name, an expression to evaluate as the ID, or an object of the form `{<sourceLayer>: <propertyName/expression>}`. The expression can only be feature dependent if it is used."
    },
    "dynamic": {
      "type": "boolean",
      "default": false,
      "doc": "Whether to optimize this source for frequent data updates (e.g. animating features).",
      "sdk-support": {
        "basic functionality": {
          "js": "3.4.0"
        }
      }
    }
  },
  "source_video": {
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "video": {
          "doc": "A video data source."
        }
      },
      "doc": "The data type of the video source."
    },
    "urls": {
      "required": true,
      "type": "array",
      "value": "string",
      "doc": "URLs to video content in order of preferred format."
    },
    "coordinates": {
      "required": true,
      "doc": "Corners of video specified in longitude, latitude pairs.",
      "type": "array",
      "length": 4,
      "value": {
        "type": "array",
        "length": 2,
        "value": "number",
        "doc": "A single longitude, latitude pair."
      }
    }
  },
  "source_image": {
    "type": {
      "required": true,
      "type": "enum",
      "values": {
        "image": {
          "doc": "An image data source."
        }
      },
      "doc": "The data type of the image source."
    },
    "url": {
      "required": false,
      "type": "string",
      "doc": "URL that points to an image. If the URL is not specified, the image is expected to be loaded directly during runtime."
    },
    "coordinates": {
      "required": true,
      "doc": "Corners of image specified in longitude, latitude pairs. Note: When using globe projection, the image will be centered at the North or South Pole in the respective hemisphere if the average latitude value exceeds 85 degrees or falls below -85 degrees.",
      "type": "array",
      "length": 4,
      "value": {
        "type": "array",
        "length": 2,
        "value": "number",
        "doc": "A single longitude, latitude pair."
      }
    }
  },
  "modelNodeOverride": {
       "orientation": {
         "type": "array",
         "value": "number",
         "length": 3,
         "default": [0, 0, 0],
         "period": 360,
         "units": "degrees",
         "doc": "Override the orientation of the model node in euler angles [x, y, z]."
       }
  },
   "modelLightOverrides": {
    "light-ambient-color": {
      "type": "color",
      "doc": "Override the color of ambient lights."
    },
    "light-ambient-intensity": {
      "type": "number",
      "doc": "Override the intensity of light-ambient-color (on a scale from 0 to 1)."
    },
     "light-directional-color": {
      "type": "color",
      "doc": "Override the color of directional lights."
    },
    "light-directional-intensity": {
      "type": "number",
      "doc": "Override the intensity of light-directional-color (on a scale from 0 to 1)."
    }
  },
  "modelNodeOverrides": {
    "*": {
       "type": "modelNodeOverride",
       "doc": "An object defining model node properties to override. Object key is the node name as defined in the model."
    }
  },
  "modelMaterialOverride": {
    "model-color": {
      "type": "color",
      "doc": "Override the tint color of the material."
    },
    "model-color-mix-intensity": {
      "type": "number",
      "doc": "Override the intensity of model-color (on a scale from 0 to 1) in color mix with original 3D model's colors."
    },
    "model-opacity": {
      "type": "number",
      "doc": "Override the opacity of the material."
    },
    "model-emissive-strength": {
      "type": "number",
      "units": "intensity",
      "doc": "Override strength of the emission of material."
    }
  },
  "modelMaterialOverrides": {
    "*": {
       "type": "modelMaterialOverride",
        "doc": "An object defining material properties to override. Object key is the material name as defined in the model."
    }
  },
  "modelSourceModel": {
      "uri": {
        "type": "string",
        "required": true,
        "doc": "A URL to a model resource. Supported protocols are `http:`, `https:`, and `mapbox://<Model ID>`."
      },
      "position": {
        "type": "array",
        "value": "number",
        "length": 2,
        "required": false,
        "default": [0,0],
        "minimum": [-180, -90],
        "maximum": [180, 90],
        "doc": "Position of the model in longitude and latitude [lng, lat]."
      },
      "orientation": {
        "type": "array",
        "value": "number",
        "length": 3,
        "required": false,
        "default": [0,0,0],
        "period": 360,
        "units": "degrees",
        "doc": "Orientation of the model in euler angles [x, y, z]."
      },
      "lightOverrides": {
        "type": "modelLightOverrides",
        "required": false,
        "doc": "A collection of light overrides."
      },
      "nodeOverrides": {
        "type": "modelNodeOverrides",
        "required": false,
        "doc": "A collection of node overrides."
      },
      "materialOverrides": {
        "type": "modelMaterialOverrides",
        "required": false,
        "doc": "A collection of material overrides."
      },
      "nodeOverrideNames": {
        "type": "array",
        "value": "string",
        "doc" : "An array of one or more model node names whose transform will be overridden from model layer paint properties.",
        "required": false
      },
      "materialOverrideNames": {
        "type": "array",
        "value": "string",
        "doc" : "An array of one or more model material names whose properties will be overridden from model layer paint properties.",
        "required": false
      },
      "featureProperties": {
        "type": "*",
        "doc": "An object defining custom properties of the model. Properties are accessible as feature properties in expressions.",
        "required": false
      }
  },
  "modelSourceModels": {
    "*": {
       "type": "modelSourceModel",
       "doc": "A 3D model and its properties."
    }
  },
  "source_model" : {
    "type": {
      "required": true,
        "type": "enum",
        "values": {
          "model": {
            "doc": "A collection of 3D models"
          },
          "batched-model": {
            "doc": "A collection of 3D models with anchor data"
          }
       },
      "doc": "Type of model source to be added. From single models to represent 2D layers to 3D tiled models covering a wide area."
    },
    "url": {
      "type": "string",
      "doc": "A URL to a TileJSON resource. Supported protocols are `http:`, `https:`, and `mapbox://<Tileset ID>`. Required if `tiles` is not provided."
    },
    "maxzoom": {
      "type": "number",
      "default": 18,
      "doc": "Maximum zoom level at which to create batched model tiles. Data from tiles at the maxzoom are used when displaying the map at higher zoom levels."
    },
    "minzoom": {
      "type": "number",
      "default": 0,
      "doc": "Minimum zoom level for which batched-model tiles are available"
    },
    "tiles": {
      "type": "array",
      "value": "string",
      "doc": "An array of one or more tile source URLs, as in the TileJSON spec. Requires `batched-model` source type."
    },
    "models": {
      "type": "modelSourceModels",
      "doc": "Defines properties of 3D models in collection. Requires `model` source type."
    }
  },
  "layer": {
    "id": {
      "type": "string",
      "doc": "Unique layer name.",
      "required": true
    },
    "type": {
      "type": "enum",
      "values": {
        "fill": {
          "doc": "A filled polygon with an optional stroked border.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.10.0",
              "android": "2.0.1",
              "ios": "2.0.0"
            }
          }
        },
        "line": {
          "doc": "A stroked line.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.10.0",
              "android": "2.0.1",
              "ios": "2.0.0"
            }
          }
        },
        "symbol": {
          "doc": "An icon or a text label.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.10.0",
              "android": "2.0.1",
              "ios": "2.0.0"
            }
          }
        },
        "circle": {
          "doc": "A filled circle.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.10.0",
              "android": "2.0.1",
              "ios": "2.0.0"
            }
          }
        },
        "heatmap": {
          "doc": "A heatmap.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.41.0",
              "android": "6.0.0",
              "ios": "4.0.0"
            }
          }
        },
        "fill-extrusion": {
          "doc": "An extruded (3D) polygon.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.27.0",
              "android": "5.1.0",
              "ios": "3.6.0"
            }
          }
        },
        "building": {
          "doc": "A procedural 3D building.",
          "sdk-support": {
            "js": "3.16.0",
            "android": "11.16.0",
            "ios": "11.16.0"
          },
          "private": true
        },
        "raster": {
          "doc": "Raster map textures such as satellite imagery.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.10.0",
              "android": "2.0.1",
              "ios": "2.0.0"
            }
          }
        },
        "raster-particle": {
          "experimental": true,
          "doc": "Particle animation driven by textures such as wind maps.",
          "sdk-support": {
            "basic functionality": {
              "js": "3.3.0",
              "android": "11.4.0",
              "ios": "11.4.0"
            }
          }
        },
        "hillshade": {
          "doc": "Client-side hillshading visualization based on DEM data. Currently, the implementation only supports Mapbox Terrain RGB and Mapzen Terrarium tiles.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.43.0",
              "android": "6.0.0",
              "ios": "4.0.0"
            }
          }
        },
        "model": {
          "doc": "A 3D model",
          "sdk-support": {
            "basic functionality": {
              "js": "3.0.0",
              "android": "11.0.0",
              "ios": "11.0.0"
            }
          }
        },
        "background": {
          "doc": "The background color or pattern of the map.",
          "sdk-support": {
            "basic functionality": {
              "js": "0.10.0",
              "android": "2.0.1",
              "ios": "2.0.0"
            }
          }
        },
        "sky": {
          "doc": "A spherical dome around the map that is always rendered behind all other layers.",
          "sdk-support": {
            "basic functionality": {
              "js": "2.0.0",
              "ios": "10.0.0",
              "android": "10.0.0"
            }
          }
        },
        "slot": {
          "doc": "Marks the position of a slot.",
          "sdk-support": {
            "basic functionality": {
              "js": "3.0.0",
              "android": "11.0.0",
              "ios": "11.0.0"
            }
          }
        },
        "placement-group": {
          "doc": "Marks a placement priority group relative to other layers, going from lower to higher priority. Symbols from groups with higher priorities will be placed first",
          "sdk-support": {
            "basic functionality": {}
          },
          "private": true,
          "experimental": true
        },
        "clip": {
          "doc": "Layer that removes 3D content from map.",
          "sdk-support": {
            "basic functionality": {
              "js": "3.5.0",
              "android": "11.6.0",
              "ios": "11.6.0"
            }
          }
        }
      },
      "doc": "Rendering type of this layer.",
      "required": true
    },
    "metadata": {
      "type": "*",
      "doc": "Arbitrary properties useful to track with the layer, but do not influence rendering. Properties should be prefixed to avoid collisions, like 'mapbox:'."
    },
    "source": {
      "type": "string",
      "doc": "Name of a source description to be used for this layer. Required for all layer types except `background`, `slot`, and `placement-group`."
    },
    "source-layer": {
      "type": "string",
      "doc": "Layer to use from a vector tile source. Required for vector and raster-array sources; prohibited for all other source types, including GeoJSON sources."
    },
    "slot": {
      "type": "string",
      "doc": "The slot this layer is assigned to. If specified, and a slot with that name exists, it will be placed at that position in the layer order.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "minzoom": {
      "type": "number",
      "minimum": 0,
      "maximum": 24,
      "doc": "The minimum zoom level for the layer. At zoom levels less than the minzoom, the layer will be hidden."
    },
    "maxzoom": {
      "type": "number",
      "minimum": 0,
      "maximum": 24,
      "doc": "The maximum zoom level for the layer. At zoom levels equal to or greater than the maxzoom, the layer will be hidden."
    },
    "filter": {
      "type": "filter",
      "doc": "An expression specifying conditions on source features. Only features that match the filter are displayed. Zoom expressions in filters are only evaluated at integer zoom levels. The `[\"feature-state\", ...]` expression is not supported in filter expressions. The `[\"pitch\"]` and `[\"distance-from-center\"]` expressions are supported only for filter expressions on the symbol layer."
    },
    "layout": {
      "type": "layout",
      "doc": "Layout properties for the layer."
    },
    "paint": {
      "type": "paint",
      "doc": "Default paint properties for this layer."
    },
    "appearances": {
      "type": "array",
      "value": "appearance",
      "supported-layer-types": ["symbol"],
      "doc": "Conditional styling applied to symbol layer features based on dynamic conditions. If multiple conditions are true, only the first matching appearance will be applied. Only properties marked with 'Works with appearances' are supported. Known issue: at the moment having both `appearances` and `text-variable-anchor` in the same layer doesn't work correctly."
    }
  },
  "appearance": {
    "condition": {
      "type": "boolean",
      "doc": "A boolean expression that determines when this appearance should be applied.",
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "pitch",
          "feature",
          "feature-state",
          "measure-light",
          "distance-from-center"
        ]
      },
      "property-type": "data-driven"
    },
    "name": {
      "type": "string",
      "doc": "Optional name for this appearance. Non-empty names should be unique within a layer.",
      "property-type": "constant"
    },
    "properties": {
      "type": "*",
      "doc": "Style properties to apply when the condition is met."
    }
  },
  "layout": [
    "layout_clip",
    "layout_fill",
    "layout_line",
    "layout_circle",
    "layout_heatmap",
    "layout_fill-extrusion",
    "layout_building",
    "layout_symbol",
    "layout_raster",
    "layout_raster-particle",
    "layout_hillshade",
    "layout_background",
    "layout_sky",
    "layout_model"
  ],
  "layout_background": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    }
  },
  "layout_sky": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    }
  },
  "layout_model": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    },
    "model-id": {
      "type": "string",
      "default": "",
      "doc": "Model to render. It can be either a string referencing an element to the models root property or an internal or external URL",
      "property-type": "data-driven",
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "transition": false,
      "requires": [
        {
          "source": [
            "geojson",
            "vector"
          ]
        }]
    },
    "model-allow-density-reduction": {
      "type": "boolean",
      "default": true,
      "transition": false,
      "experimental": true,
      "doc": "If true, the models will be reduced in density based on the zoom level. This is useful for large datasets that may be slow to render.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "property-type": "data-constant"
    }
  },
  "layout_clip": {
    "clip-layer-types": {
      "type": "array",
      "value": "enum",
      "values": {
        "model": {
          "doc": "If present the clip layer would remove all 3d model layers below it. Currently only instanced models (e.g. trees) are removed."
        },
        "symbol": {
          "doc": "If present the clip layer would remove all symbol layers below it."
        }
      },
      "default": [],
      "doc": "Layer types that will also be removed if fallen below this clip layer.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0",
          "android": "11.6.0",
          "ios": "11.6.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "clip-layer-scope": {
      "type": "array",
      "value": "string",
      "default": [],
      "doc": "Removes content from layers with the specified scope. By default all layers are affected. For example specifying `basemap` will only remove content from the Mapbox Standard style layers which have the same scope",
      "sdk-support": {
        "basic functionality": {
          "js": "3.6.0",
          "android": "11.7.0",
          "ios": "11.7.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.18.0",
          "android": "11.18.0",
          "ios": "11.18.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    }
  },
  "layout_fill": {
    "fill-sort-key": {
      "type": "number",
      "doc": "Sorts features in ascending order based on this value. Features with a higher sort key will appear above features with a lower sort key.",
      "sdk-support": {
        "basic functionality": {
          "js": "1.2.0",
          "android": "9.1.0",
          "ios": "5.8.0"
        },
        "data-driven styling": {
          "js": "1.2.0",
          "android": "9.1.0",
          "ios": "5.8.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    },
    "fill-elevation-reference": {
      "type": "enum",
      "doc": "Selects the base of fill-elevation. Some modes might require precomputed elevation data in the tileset.",
      "values": {
        "none": {
          "doc": "Elevated rendering is disabled."
        },
        "hd-road-base": {
          "doc": "Elevate geometry relative to HD roads. Use this mode to describe base polygons of the road networks."
        },
        "hd-road-markup": {
          "doc": "Elevated rendering is enabled. Use this mode to describe additive and stackable features such as 'hatched areas' that should exist only on top of road polygons."
      }
      },
      "default": "none",
      "experimental": true,
      "private": true,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "fill-construct-bridge-guard-rail": {
      "type": "boolean",
      "doc": "Determines whether bridge guard rails are added for elevated roads.",
      "default": "true",
      "transition": false,
      "experimental": true,
      "private": true,
      "sdk-support": {
        "basic functionality": {
            "android": "11.11.0",
            "ios": "11.11.0",
            "js": "3.14.0"
        },
        "data-driven styling": {
          "android": "11.11.0",
          "ios": "11.11.0",
          "js": "3.14.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    }
  },
  "layout_circle": {
    "circle-sort-key": {
      "type": "number",
      "doc": "Sorts features in ascending order based on this value. Features with a higher sort key will appear above features with a lower sort key.",
      "sdk-support": {
        "basic functionality": {
          "js": "1.2.0",
          "android": "9.2.0",
          "ios": "5.9.0"
        },
        "data-driven styling": {
          "js": "1.2.0",
          "android": "9.2.0",
          "ios": "5.9.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "circle-elevation-reference": {
      "type": "enum",
      "doc": "Selects the base of circle-elevation. Some modes might require precomputed elevation data in the tileset.",
      "values": {
        "none": {
          "doc": "Elevated rendering is disabled."
        },
        "hd-road-markup": {
          "doc": "Elevated rendering is enabled. Use this mode to describe additive and stackable features that should exist only on top of road polygons."
        }
      },
      "default": "none",
      "experimental": true,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "android": "11.11.0",
          "ios": "11.11.0",
          "js": "3.14.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    }
  },
  "layout_heatmap": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.41.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    }
  },
  "layout_fill-extrusion": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    },
    "fill-extrusion-edge-radius": {
      "type": "number",
      "experimental": true,
      "default": 0,
      "minimum": 0,
      "maximum": 1,
      "doc": "Radius of a fill extrusion edge in meters. If not zero, rounds extrusion edges for a smoother appearance.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "10.7.0",
          "ios": "10.7.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    },
    "source-max-zoom": {
      "type": "number",
      "minimum": 0,
      "maximum": 24,
      "private": true,
      "experimental": true,
      "doc": "The maximum zoom level at which to load and parse source tiles for this layer. When set, tiles are never loaded or re-parsed above this zoom level. At higher zoom levels, the renderer reuses tiles from this zoom level without creating overscaled variants. This reduces the number of tiles parsed and prevents tile-border artifacts for layers like fill-extrusion.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.21.0",
          "android": "11.21.0",
          "ios": "11.21.0"
        }
      },
      "property-type": "constant"
    }
  },
  "layout_building": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    },
    "building-facade": {
      "type": "boolean",
      "default": false,
      "doc": "Whether to render facade details on the buildings.",
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "building-facade-floors": {
      "type": "number",
      "minimum": 1,
      "maximum": 200,
      "default": 3,
      "doc": "Number of floors created when building-facade is enabled.",
      "private": true,
      "property-type": "data-driven",
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "feature"
        ]
      },
      "requires": [
        "building-facade"
      ]
    },
    "building-facade-unit-width": {
      "type": "number",
      "minimum": 1.0,
      "maximum": 20.0,
      "default": 3.1,
      "doc": "Width of a floor. Think of this as measure how wide each unit should be. This effectively determines the number of units per each floor. Note that this does not affect the ground level facades (i.e. number of windows).",
      "private": true,
      "property-type": "data-driven",
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "feature"
        ]
      },
      "requires": [
        "building-facade"
      ]
    },
    "building-facade-window": {
      "type": "array",
      "length": 2,
      "value": "number",
      "minimum": 0.1,
      "maximum": 1.0,
      "default": [0.9, 0.9],
      "doc": "Given as fractions, specifies the percentage of unit area covered by windows when building-facade is enabled. Note that the area of a unit is ultimately determined by `building-facade-unit-width` and `building-facade-floors`.",
      "private": true,
      "property-type": "data-driven",
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "feature"
        ]
      },
      "requires": [
        "building-facade"
      ]
    },
    "building-roof-shape": {
      "type": "enum",
      "values": {
        "flat": {
          "doc": "Use flat roof for the procedurally generated buildings."
        },
        "hipped": {
          "doc": "Use hipped roof for the procedurally generated buildings."
        },
        "gabled": {
          "doc": "Use gabled roof for the procedurally generated buildings."
        },
        "parapet": {
          "doc": "Use parapet roof for the procedurally generated buildings."
        },
        "mansard": {
          "doc": "Use mansard roof for the procedurally generated buildings."
        },
        "skillion": {
          "doc": "Use skillion roof for the procedurally generated buildings."
        },
        "pyramidal": {
          "doc": "Use pyramidal roof for the procedurally generated buildings."
        }
      },
      "default": "flat",
      "doc": "Roof type to use for the procedural buildings.",
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "building-height": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "units": "meters",
      "doc": "The height of the procedural buildings.",
      "transition": true,
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-driven"
    },
    "building-base": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "units": "meters",
      "doc": "The height with which to extrude the base of this layer. Must be less than or equal to `building-height`.",
      "transition": true,
      "requires": [
        "building-height"
      ],
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-driven"
    },
    "building-flood-light-wall-radius": {
      "property-type": "data-driven",
      "type": "number",
      "private": true,
      "units": "meters",
      "default": 0,
      "minimum": 0,
      "doc": "The extent of the flood light effect on the walls of the buildings in meters.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "feature",
          "feature-state"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-flood-light-ground-radius": {
      "property-type": "data-driven",
      "type": "number",
      "private": true,
      "units": "meters",
      "default": 0,
      "doc": "The extent of the flood light effect on the ground beneath the buildings in meters.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "feature",
          "feature-state"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-flip-roof-orientation": {
      "property-type": "data-driven",
      "type": "boolean",
      "private": true,
      "units": "meters",
      "default": false,
      "doc": "Flips the orientation of the roofs for the buildings. This only affects simple geometries. Namely buildings whose footprints form a quadrilateral. By default (false), the roof ridge takes the direction of the longer edge of the quadrilateral.",
      "transition": true,
      "requires": [
        "building-roof-shape"
      ],
      "expression": {
        "interpolated": true,
        "parameters": [
          "feature",
          "feature-state"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    }
  },
  "layout_line": {
    "line-cap": {
      "type": "enum",
      "values": {
        "butt": {
          "doc": "A cap with a squared-off end which is drawn to the exact endpoint of the line."
        },
        "round": {
          "doc": "A cap with a rounded end which is drawn beyond the endpoint of the line at a radius of one-half of the line's width and centered on the endpoint of the line."
        },
        "square": {
          "doc": "A cap with a squared-off end which is drawn beyond the endpoint of the line at a distance of one-half of the line's width."
        }
      },
      "default": "butt",
      "doc": "The display of line endings.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "2.3.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "line-join": {
      "type": "enum",
      "values": {
        "bevel": {
          "doc": "A join with a squared-off end which is drawn beyond the endpoint of the line at a distance of one-half of the line's width."
        },
        "round": {
          "doc": "A join with a rounded end which is drawn beyond the endpoint of the line at a radius of one-half of the line's width and centered on the endpoint of the line."
        },
        "miter": {
          "doc": "A join with a sharp, angled corner which is drawn with the outer sides beyond the endpoint of the path until they meet."
        },
        "none": {
          "doc": "Line segments are not joined together, each one creates a separate line. Useful in combination with line-pattern. Line-cap property is not respected. Can't be used with data-driven styling."
        }
      },
      "default": "miter",
      "doc": "The display of lines when joining.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.40.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        },
        "`none` value": {
          "js": "3.4.0",
          "android": "11.5.0",
          "ios": "11.5.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "line-miter-limit": {
      "type": "number",
      "default": 2,
      "doc": "Used to automatically convert miter joins to bevel joins for sharp angles.",
      "requires": [
        {
          "line-join": "miter"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "line-round-limit": {
      "type": "number",
      "default": 1.05,
      "doc": "Used to automatically convert round joins to miter joins for shallow angles.",
      "requires": [
        {
          "line-join": "round"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "line-sort-key": {
      "type": "number",
      "doc": "Sorts features in ascending order based on this value. Features with a higher sort key will appear above features with a lower sort key.",
      "sdk-support": {
        "basic functionality": {
          "js": "1.2.0",
          "android": "9.1.0",
          "ios": "5.8.0"
        },
        "data-driven styling": {
          "js": "1.2.0",
          "android": "9.1.0",
          "ios": "5.8.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "line-z-offset": {
      "type": "number",
      "doc": "Vertical offset from ground, in meters. Not supported for globe projection at the moment.",
      "default": 0,
      "requires": [
        "line-elevation-reference"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.5.0"
        },
        "data-driven styling": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.5.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature",
          "line-progress"
        ]
      },
      "property-type": "data-driven"
    },
    "line-elevation-reference": {
      "type": "enum",
      "doc": "Selects the base of line-elevation. Some modes might require precomputed elevation data in the tileset.",
      "values": {
        "none": {
          "doc": "Elevated rendering is disabled."
        },
        "sea": {
          "doc": "Elevated rendering is enabled. Use this mode to elevate lines relative to the sea level."
        },
        "ground": {
          "doc": "Elevated rendering is enabled. Use this mode to elevate lines relative to the ground's height below them."
        },
        "hd-road-markup": {
          "doc": "Elevated rendering is enabled. Use this mode to describe additive and stackable features that should exist only on top of road polygons."
        }
      },
      "default": "none",
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.8.0",
          "android": "11.9.0",
          "ios": "11.9.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "line-cross-slope": {
      "type": "number",
      "experimental": true,
      "doc": "Defines the slope of an elevated line. A value of 0 creates a horizontal line. A value of 1 creates a vertical line. Other values are currently not supported. If undefined, the line follows the terrain slope. This is an experimental property with some known issues:\n * Vertical lines don't support line caps \n * `line-join: round` is not supported with this property",
      "requires": [
        "line-z-offset"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.8.0",
          "android": "11.9.0",
          "ios": "11.9.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    },
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    },
    "line-width-unit": {
      "type": "enum",
      "doc": "Selects the unit of line-width. The same unit is automatically used for line-blur and line-offset. Note: This is an experimental property and might be removed in a future release.",
      "values": {
        "pixels": {
          "doc": "Width is rendered in pixels."
        },
        "meters": {
          "doc": "Width is rendered in meters."
        }
      },
      "default": "pixels",
      "experimental": true,
      "private": true,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.9.0",
          "android": "11.9.0",
          "ios": "11.9.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "line-elevation-ground-scale": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "maximum": 1,
      "doc": "Controls how much the elevation of lines with `line-elevation-reference` set to `sea` scales with terrain exaggeration. A value of 0 keeps the line at a fixed altitude above sea level. A value of 1 scales the elevation proportionally with terrain exaggeration.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.19.0",
          "android": "11.19.0",
          "ios": "11.19.0"
        },
        "data-driven styling": {
          "js": "3.19.0",
          "android": "11.19.0",
          "ios": "11.19.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "line-progress"
        ]
      },
      "requires": [
        "line-z-offset"
      ],
      "transition": true,
      "property-type": "data-driven"
    }
  },
  "layout_symbol": {
    "symbol-placement": {
      "type": "enum",
      "values": {
        "point": {
          "doc": "The label is placed at the point where the geometry is located."
        },
        "line": {
          "doc": "The label is placed along the line of the geometry. Can only be used on `LineString` and `Polygon` geometries."
        },
        "line-center": {
          "doc": "The label is placed at the center of the line of the geometry. Can only be used on `LineString` and `Polygon` geometries. Note that a single feature in a vector tile may contain multiple line geometries."
        }
      },
      "default": "point",
      "doc": "Label placement relative to its geometry.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "`line-center` value": {
          "js": "0.47.0",
          "android": "6.4.0",
          "ios": "4.3.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "symbol-spacing": {
      "type": "number",
      "default": 250,
      "minimum": 1,
      "units": "pixels",
      "doc": "Distance between two symbol anchors.",
      "requires": [
        {
          "symbol-placement": "line"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "symbol-avoid-edges": {
      "type": "boolean",
      "default": false,
      "doc": "If true, the symbols will not cross tile edges to avoid mutual collisions. Recommended in layers that don't have enough padding in the vector tile to prevent collisions, or if it is a point symbol layer placed after a line symbol layer. When using a client that supports global collision detection, like Mapbox GL JS version 0.42.0 or greater, enabling this property is not needed to prevent clipped labels at tile boundaries.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "symbol-sort-key": {
      "type": "number",
      "doc": "Sorts features in ascending order based on this value. Features with lower sort keys are drawn and placed first. When `icon-allow-overlap` or `text-allow-overlap` is `false`, features with a lower sort key will have priority during placement. When `icon-allow-overlap` or `text-allow-overlap` is set to `true`, features with a higher sort key will overlap over features with a lower sort key.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.53.0",
          "android": "7.4.0",
          "ios": "4.11.0"
        },
        "data-driven styling": {
          "js": "0.53.0",
          "android": "7.4.0",
          "ios": "4.11.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "symbol-z-order": {
      "type": "enum",
      "values": {
        "auto": {
          "doc": "Sorts symbols by `symbol-sort-key` if set. Otherwise, sorts symbols by their y-position relative to the viewport if `icon-allow-overlap` or `text-allow-overlap` is set to `true` or `icon-ignore-placement` or `text-ignore-placement` is `false`."
        },
        "viewport-y": {
          "doc": "Sorts symbols by their y-position relative to the viewport if any of the following is set to `true`: `icon-allow-overlap`, `text-allow-overlap`, `icon-ignore-placement`, `text-ignore-placement`."
        },
        "source": {
          "doc": "Sorts symbols by `symbol-sort-key` if set. Otherwise, no sorting is applied; symbols are rendered in the same order as the source data."
        }
      },
      "default": "auto",
      "doc": "Determines whether overlapping symbols in the same layer are rendered in the order that they appear in the data source or by their y-position relative to the viewport. To control the order and prioritization of symbols otherwise, use `symbol-sort-key`.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.49.0",
          "android": "6.6.0",
          "ios": "4.5.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "symbol-z-elevate": {
      "type": "boolean",
      "default": false,
      "doc": "Position symbol on buildings (both fill extrusions and models) rooftops. In order to have minimal impact on performance, this is supported only when `fill-extrusion-height` is not zoom-dependent and remains unchanged. For fading in buildings when zooming in, fill-extrusion-vertical-scale should be used and symbols would raise with building rooftops. Symbols are sorted by elevation, except in cases when `viewport-y` sorting or `symbol-sort-key` are applied.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "requires": [
        {
          "symbol-placement": [
            "point"
          ]
        },
        {
          "symbol-z-order": [
            "auto"
          ]
        }
      ],
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "symbol-elevation-reference": {
      "type": "enum",
      "doc": "Selects the base of symbol-elevation.",
      "values": {
        "sea": {
          "doc": "Elevate symbols relative to the sea level."
        },
        "ground": {
          "doc": "Elevate symbols relative to the ground's height below them."
        },
        "hd-road-markup": {
          "doc": "Use this mode to enable elevated behavior for features that are rendered on top of 3D road polygons. The feature is currently being developed."
        }
      },
      "default": "ground",
      "experimental": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.7.0",
          "android": "11.9.0",
          "ios": "11.9.0"
        }
      },
      "requires": [
        {
          "symbol-z-elevate": true
        }
      ],
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-allow-overlap": {
      "type": "boolean",
      "default": false,
      "doc": "If true, the icon will be visible even if it collides with other previously drawn symbols.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-ignore-placement": {
      "type": "boolean",
      "default": false,
      "doc": "If true, other symbols can be visible even if they collide with the icon.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-optional": {
      "type": "boolean",
      "default": false,
      "doc": "If true, text will display without their corresponding icons when the icon collides with other symbols and the text does not.",
      "requires": [
        "icon-image",
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-rotation-alignment": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "When `symbol-placement` is set to `point`, aligns icons east-west. When `symbol-placement` is set to `line` or `line-center`, aligns icon x-axes with the line."
        },
        "viewport": {
          "doc": "Produces icons whose x-axes are aligned with the x-axis of the viewport, regardless of the value of `symbol-placement`."
        },
        "auto": {
          "doc": "When `symbol-placement` is set to `point`, this is equivalent to `viewport`. When `symbol-placement` is set to `line` or `line-center`, this is equivalent to `map`."
        }
      },
      "default": "auto",
      "doc": "In combination with `symbol-placement`, determines the rotation behavior of icons.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "`auto` value": {
          "js": "0.25.0",
          "android": "4.2.0",
          "ios": "3.4.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-size": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "units": "factor of the original icon size",
      "doc": "Scales the original size of the icon by the provided factor. The new pixel size of the image will be the original pixel size multiplied by `icon-size`. 1 is the original size; 3 triples the size of the image.",
      "appearance": true,
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.35.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "icon-size-scale-range": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0.8,
        2
      ],
      "doc": "Limits the possible scaling range for `icon-size`, `icon-halo-width`, `icon-halo-blur` properties to be within [min-scale, max-scale]",
      "minimum": [0.1, 0.1],
      "maximum": [10, 10],
      "experimental": true,
      "expression": {
        "interpolated": false
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.8.0"
        }
      },
      "property-type": "data-constant"
    },
    "icon-text-fit": {
      "type": "enum",
      "values": {
        "none": {
          "doc": "The icon is displayed at its intrinsic aspect ratio."
        },
        "width": {
          "doc": "The icon is scaled in the x-dimension to fit the width of the text."
        },
        "height": {
          "doc": "The icon is scaled in the y-dimension to fit the height of the text."
        },
        "both": {
          "doc": "The icon is scaled in both x- and y-dimensions."
        }
      },
      "default": "none",
      "doc": "Scales the icon to fit around the associated text.",
      "requires": [
        "icon-image",
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.21.0",
          "android": "4.2.0",
          "ios": "3.4.0"
        },
        "stretchable icons": {
          "js": "1.6.0",
          "android": "9.2.0",
          "ios": "5.8.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "icon-text-fit-padding": {
      "type": "array",
      "value": "number",
      "length": 4,
      "default": [
        0,
        0,
        0,
        0
      ],
      "units": "pixels",
      "doc": "Size of the additional area added to dimensions determined by `icon-text-fit`, in clockwise order: top, right, bottom, left.",
      "requires": [
        "icon-image",
        "text-field",
        {
          "icon-text-fit": [
            "both",
            "width",
            "height"
          ]
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.21.0",
          "android": "4.2.0",
          "ios": "3.4.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "icon-image": {
      "type": "resolvedImage",
      "doc": "Name of image in sprite to use for drawing an image background.",
      "tokens": true,
      "appearance": true,
      "use-theme": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.35.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "icon-rotate": {
      "type": "number",
      "default": 0,
      "period": 360,
      "units": "degrees",
      "doc": "Rotates the icon clockwise.",
      "appearance": true,
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.21.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "icon-padding": {
      "type": "number",
      "default": 2,
      "minimum": 0,
      "units": "pixels",
      "doc": "Size of the additional area around the icon bounding box used for detecting symbol collisions.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-keep-upright": {
      "type": "boolean",
      "default": false,
      "doc": "If true, the icon may be flipped to prevent it from being rendered upside-down.",
      "requires": [
        "icon-image",
        {
          "icon-rotation-alignment": "map"
        },
        {
          "symbol-placement": [
            "line",
            "line-center"
          ]
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-offset": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0,
        0
      ],
      "doc": "Offset distance of icon from its anchor. Positive values indicate right and down, while negative values indicate left and up. Each component is multiplied by the value of `icon-size` to obtain the final offset in pixels. When combined with `icon-rotate` the offset will be as if the rotated direction was up.",
      "appearance": true,
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "icon-anchor": {
      "type": "enum",
      "values": {
        "center": {
          "doc": "The center of the icon is placed closest to the anchor."
        },
        "left": {
          "doc": "The left side of the icon is placed closest to the anchor."
        },
        "right": {
          "doc": "The right side of the icon is placed closest to the anchor."
        },
        "top": {
          "doc": "The top of the icon is placed closest to the anchor."
        },
        "bottom": {
          "doc": "The bottom of the icon is placed closest to the anchor."
        },
        "top-left": {
          "doc": "The top left corner of the icon is placed closest to the anchor."
        },
        "top-right": {
          "doc": "The top right corner of the icon is placed closest to the anchor."
        },
        "bottom-left": {
          "doc": "The bottom left corner of the icon is placed closest to the anchor."
        },
        "bottom-right": {
          "doc": "The bottom right corner of the icon is placed closest to the anchor."
        }
      },
      "default": "center",
      "doc": "Part of the icon placed closest to the anchor.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.40.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        },
        "data-driven styling": {
          "js": "0.40.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "icon-pitch-alignment": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The icon is aligned to the plane of the map."
        },
        "viewport": {
          "doc": "The icon is aligned to the plane of the viewport."
        },
        "auto": {
          "doc": "Automatically matches the value of `icon-rotation-alignment`."
        }
      },
      "default": "auto",
      "doc": "Orientation of icon when map is pitched.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.39.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-pitch-alignment": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The text is aligned to the plane of the map."
        },
        "viewport": {
          "doc": "The text is aligned to the plane of the viewport."
        },
        "auto": {
          "doc": "Automatically matches the value of `text-rotation-alignment`."
        }
      },
      "default": "auto",
      "doc": "Orientation of text when map is pitched.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.21.0",
          "android": "4.2.0",
          "ios": "3.4.0"
        },
        "`auto` value": {
          "js": "0.25.0",
          "android": "4.2.0",
          "ios": "3.4.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-rotation-alignment": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "When `symbol-placement` is set to `point`, aligns text east-west. When `symbol-placement` is set to `line` or `line-center`, aligns text x-axes with the line."
        },
        "viewport": {
          "doc": "Produces glyphs whose x-axes are aligned with the x-axis of the viewport, regardless of the value of `symbol-placement`."
        },
        "auto": {
          "doc": "When `symbol-placement` is set to `point`, this is equivalent to `viewport`. When `symbol-placement` is set to `line` or `line-center`, this is equivalent to `map`."
        }
      },
      "default": "auto",
      "doc": "In combination with `symbol-placement`, determines the rotation behavior of the individual glyphs forming the text.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "`auto` value": {
          "js": "0.25.0",
          "android": "4.2.0",
          "ios": "3.4.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-field": {
      "type": "formatted",
      "default": "",
      "tokens": true,
      "doc": "Value to use for a text label. If a plain `string` is provided, it will be treated as a `formatted` with default/inherited formatting options. SDF images are not supported in formatted text and will be ignored.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-font": {
      "type": "array",
      "value": "string",
      "default": [
        "Open Sans Regular",
        "Arial Unicode MS Regular"
      ],
      "doc": "Font stack to use for displaying text.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-size": {
      "type": "number",
      "default": 16,
      "minimum": 0,
      "units": "pixels",
      "doc": "Font size.",
      "requires": [
        "text-field"
      ],
      "appearance": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.35.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-size-scale-range": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0.8,
        2
      ],
      "doc": "Limits the possible scaling range for `text-size`, `text-halo-width`, `text-halo-blur` properties to be within [min-scale, max-scale]",
      "minimum": [0.1, 0.1],
      "maximum": [10, 10],
      "experimental": true,
      "expression": {
        "interpolated": false
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.8.0"
        }
      },
      "property-type": "data-constant"
    },
    "text-max-width": {
      "type": "number",
      "default": 10,
      "minimum": 0,
      "units": "ems",
      "doc": "The maximum line width for text wrapping.",
      "requires": [
        "text-field",
        {
          "symbol-placement": [
            "point"
          ]
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.40.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-line-height": {
      "type": "number",
      "default": 1.2,
      "units": "ems",
      "doc": "Text leading value for multi-line text.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "2.3.0",
          "android": "10.0.0",
          "ios": "10.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-letter-spacing": {
      "type": "number",
      "default": 0,
      "units": "ems",
      "doc": "Text tracking amount.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.40.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-justify": {
      "type": "enum",
      "values": {
        "auto": {
          "doc": "The text is aligned towards the anchor position."
        },
        "left": {
          "doc": "The text is aligned to the left."
        },
        "center": {
          "doc": "The text is centered."
        },
        "right": {
          "doc": "The text is aligned to the right."
        }
      },
      "default": "center",
      "doc": "Text justification options.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.39.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        },
        "auto": {
          "js": "0.54.0",
          "android": "7.4.0",
          "ios": "4.10.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-radial-offset": {
      "type": "number",
      "units": "ems",
      "default": 0,
      "doc": "Radial offset of text, in the direction of the symbol's anchor. Useful in combination with `text-variable-anchor`, which defaults to using the two-dimensional `text-offset` if present.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.54.0",
          "android": "7.4.0",
          "ios": "4.10.0"
        },
        "data-driven styling": {
          "js": "0.54.0",
          "android": "7.4.0",
          "ios": "4.10.0"
        }
      },
      "requires": [
        "text-field"
      ],
      "property-type": "data-driven",
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      }
    },
    "text-variable-anchor": {
      "type": "array",
      "value": "enum",
      "values": {
        "center": {
          "doc": "The center of the text is placed closest to the anchor."
        },
        "left": {
          "doc": "The left side of the text is placed closest to the anchor."
        },
        "right": {
          "doc": "The right side of the text is placed closest to the anchor."
        },
        "top": {
          "doc": "The top of the text is placed closest to the anchor."
        },
        "bottom": {
          "doc": "The bottom of the text is placed closest to the anchor."
        },
        "top-left": {
          "doc": "The top left corner of the text is placed closest to the anchor."
        },
        "top-right": {
          "doc": "The top right corner of the text is placed closest to the anchor."
        },
        "bottom-left": {
          "doc": "The bottom left corner of the text is placed closest to the anchor."
        },
        "bottom-right": {
          "doc": "The bottom right corner of the text is placed closest to the anchor."
        }
      },
      "requires": [
        "text-field",
        {
          "symbol-placement": [
            "point"
          ]
        }
      ],
      "doc": "To increase the chance of placing high-priority labels on the map, you can provide an array of `text-anchor` locations: the renderer will attempt to place the label at each location, in order, before moving onto the next label. Use `text-justify: auto` to choose justification based on anchor position. To apply an offset, use the `text-radial-offset` or the two-dimensional `text-offset`. Known issue: at the moment having both `appearances` and `text-variable-anchor` in the same layer doesn't work correctly.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.54.0",
          "android": "7.4.0",
          "ios": "4.10.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-anchor": {
      "type": "enum",
      "values": {
        "center": {
          "doc": "The center of the text is placed closest to the anchor."
        },
        "left": {
          "doc": "The left side of the text is placed closest to the anchor."
        },
        "right": {
          "doc": "The right side of the text is placed closest to the anchor."
        },
        "top": {
          "doc": "The top of the text is placed closest to the anchor."
        },
        "bottom": {
          "doc": "The bottom of the text is placed closest to the anchor."
        },
        "top-left": {
          "doc": "The top left corner of the text is placed closest to the anchor."
        },
        "top-right": {
          "doc": "The top right corner of the text is placed closest to the anchor."
        },
        "bottom-left": {
          "doc": "The bottom left corner of the text is placed closest to the anchor."
        },
        "bottom-right": {
          "doc": "The bottom right corner of the text is placed closest to the anchor."
        }
      },
      "default": "center",
      "doc": "Part of the text placed closest to the anchor.",
      "requires": [
        "text-field",
        {
          "!": "text-variable-anchor"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.39.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-max-angle": {
      "type": "number",
      "default": 45,
      "units": "degrees",
      "doc": "Maximum angle change between adjacent characters.",
      "requires": [
        "text-field",
        {
          "symbol-placement": [
            "line",
            "line-center"
          ]
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-writing-mode": {
      "type": "array",
      "value": "enum",
      "values": {
        "horizontal": {
          "doc": "If a text's language supports horizontal writing mode, symbols would be laid out horizontally."
        },
        "vertical": {
          "doc": "If a text's language supports vertical writing mode, symbols would be laid out vertically."
        }
      },
      "doc": "The property allows control over a symbol's orientation. Note that the property values act as a hint, so that a symbol whose language doesn’t support the provided orientation will be laid out in its natural orientation. Example: English point symbol will be rendered horizontally even if array value contains single 'vertical' enum value. For symbol with point placement, the order of elements in an array define priority order for the placement of an orientation variant. For symbol with line placement, the default text writing mode is either ['horizontal', 'vertical'] or ['vertical', 'horizontal'], the order doesn't affect the placement.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "1.3.0",
          "android": "8.3.0",
          "ios": "5.3.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-rotate": {
      "type": "number",
      "default": 0,
      "period": 360,
      "units": "degrees",
      "doc": "Rotates the text clockwise.",
      "requires": [
        "text-field"
      ],
      "appearance": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.35.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-padding": {
      "type": "number",
      "default": 2,
      "minimum": 0,
      "units": "pixels",
      "doc": "Size of the additional area around the text bounding box used for detecting symbol collisions.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-keep-upright": {
      "type": "boolean",
      "default": true,
      "doc": "If true, the text may be flipped vertically to prevent it from being rendered upside-down.",
      "requires": [
        "text-field",
        {
          "text-rotation-alignment": "map"
        },
        {
          "symbol-placement": [
            "line",
            "line-center"
          ]
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-transform": {
      "type": "enum",
      "values": {
        "none": {
          "doc": "The text is not altered."
        },
        "uppercase": {
          "doc": "Forces all letters to be displayed in uppercase."
        },
        "lowercase": {
          "doc": "Forces all letters to be displayed in lowercase."
        }
      },
      "default": "none",
      "doc": "Specifies how to capitalize text, similar to the CSS `text-transform` property.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-offset": {
      "type": "array",
      "doc": "Offset distance of text from its anchor. Positive values indicate right and down, while negative values indicate left and up. If used with text-variable-anchor, input values will be taken as absolute values. Offsets along the x- and y-axis will be applied automatically based on the anchor position.",
      "value": "number",
      "units": "ems",
      "length": 2,
      "default": [
        0,
        0
      ],
      "requires": [
        "text-field",
        {
          "!": "text-radial-offset"
        }
      ],
      "appearance": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.35.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "text-allow-overlap": {
      "type": "boolean",
      "default": false,
      "doc": "If true, the text will be visible even if it collides with other previously drawn symbols.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-ignore-placement": {
      "type": "boolean",
      "default": false,
      "doc": "If true, other symbols can be visible even if they collide with the text.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-optional": {
      "type": "boolean",
      "default": false,
      "doc": "If true, icons will display without their corresponding text when the text collides with other symbols and the icon does not.",
      "requires": [
        "text-field",
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    }
  },
  "layout_raster": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    }
  },
  "layout_raster-particle": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        },
        "expressions support": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    }
  },
  "layout_hillshade": {
    "visibility": {
      "type": "enum",
      "values": {
        "visible": {
          "doc": "The layer is shown."
        },
        "none": {
          "doc": "The layer is not shown."
        }
      },
      "default": "visible",
      "doc": "Whether this layer is displayed.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        },
        "expressions support": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "constant"
    }
  },
  "filter": {
    "type": "array",
    "value": "*",
    "doc": "A filter selects specific features from a layer."
  },
  "filter_symbol": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to display a symbol. Symbols support dynamic filtering, meaning this expression can use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature", "pitch", "distance-from-center"]
    }
  },
  "filter_fill": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to display a polygon. Fill layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_hillshade": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to enable the hillshade layer. Hillshade layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_raster": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to enable the raster layer. Raster layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_raster-particle": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to enable the raster particle layer. Raster particle layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_clip": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to enable the clip layer. Clip layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_model": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to display a model. Model layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_line": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to display a Polygon or LineString. Line layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_circle": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to display a circle. Circle layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_fill-extrusion": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to display a Polygon. Fill-extrusion layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_building": {
    "type": "boolean",
    "doc": "Expression which determines whether or not to display a Polygon. Building layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_heatmap": {
    "type": "boolean",
    "doc": "Expression used to determine whether a point is being displayed or not. Heatmap layer does NOT support dynamic filtering, meaning this expression can NOT use the `[\"pitch\"]` and `[\"distance-from-center\"]` expressions to reference the current state of the view.",
    "default": false,
    "transition": false,
    "property-type": "data-driven",
    "expression": {
      "interpolated": false,
      "parameters": ["zoom", "feature"]
    }
  },
  "filter_operator": {
    "type": "enum",
    "values": {
      "==": {
        "doc": "`[\"==\", key, value]` equality: `feature[key] = value`"
      },
      "!=": {
        "doc": "`[\"!=\", key, value]` inequality: `feature[key] ≠ value`"
      },
      ">": {
        "doc": "`[\">\", key, value]` greater than: `feature[key] > value`"
      },
      ">=": {
        "doc": "`[\">=\", key, value]` greater than or equal: `feature[key] ≥ value`"
      },
      "<": {
        "doc": "`[\"<\", key, value]` less than: `feature[key] < value`"
      },
      "<=": {
        "doc": "`[\"<=\", key, value]` less than or equal: `feature[key] ≤ value`"
      },
      "in": {
        "doc": "`[\"in\", key, v0, ..., vn]` set inclusion: `feature[key] ∈ {v0, ..., vn}`"
      },
      "!in": {
        "doc": "`[\"!in\", key, v0, ..., vn]` set exclusion: `feature[key] ∉ {v0, ..., vn}`"
      },
      "all": {
        "doc": "`[\"all\", f0, ..., fn]` logical `AND`: `f0 ∧ ... ∧ fn`"
      },
      "any": {
        "doc": "`[\"any\", f0, ..., fn]` logical `OR`: `f0 ∨ ... ∨ fn`"
      },
      "none": {
        "doc": "`[\"none\", f0, ..., fn]` logical `NOR`: `¬f0 ∧ ... ∧ ¬fn`"
      },
      "has": {
        "doc": "`[\"has\", key]` `feature[key]` exists"
      },
      "!has": {
        "doc": "`[\"!has\", key]` `feature[key]` does not exist"
      }
    },
    "doc": "The filter operator."
  },
  "geometry_type": {
    "type": "enum",
    "values": {
      "Point": {
        "doc": "Filter to point geometries."
      },
      "LineString": {
        "doc": "Filter to line geometries."
      },
      "Polygon": {
        "doc": "Filter to polygon geometries."
      }
    },
    "doc": "The geometry type for the filter to select."
  },
  "function": {
    "expression": {
      "type": "expression",
      "doc": "An expression."
    },
    "stops": {
      "type": "array",
      "doc": "An array of stops.",
      "value": "function_stop"
    },
    "base": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "doc": "The exponential base of the interpolation curve. It controls the rate at which the result increases. Higher values make the result increase more towards the high end of the range. With `1` the stops are interpolated linearly."
    },
    "property": {
      "type": "string",
      "doc": "The name of a feature property to use as the function input.",
      "default": "$zoom"
    },
    "type": {
      "type": "enum",
      "values": {
        "identity": {
          "doc": "Return the input value as the output value."
        },
        "exponential": {
          "doc": "Generate an output by interpolating between stops just less than and just greater than the function input."
        },
        "interval": {
          "doc": "Return the output value of the stop just less than the function input."
        },
        "categorical": {
          "doc": "Return the output value of the stop equal to the function input."
        }
      },
      "doc": "The interpolation strategy to use in function evaluation.",
      "default": "exponential"
    },
    "colorSpace": {
      "type": "enum",
      "values": {
        "rgb": {
          "doc": "Use the RGB color space to interpolate color values"
        },
        "lab": {
          "doc": "Use the LAB color space to interpolate color values."
        },
        "hcl": {
          "doc": "Use the HCL color space to interpolate color values, interpolating the Hue, Chroma, and Luminance channels individually."
        }
      },
      "doc": "The color space in which colors interpolated. Interpolating colors in perceptual color spaces like LAB and HCL tend to produce color ramps that look more consistent and produce colors that can be differentiated more easily than those interpolated in RGB space.",
      "default": "rgb"
    },
    "default": {
      "type": "*",
      "required": false,
      "doc": "A value to serve as a fallback function result when a value isn't otherwise available. It is used in the following circumstances:\n* In categorical functions, when the feature value does not match any of the stop domain values.\n* In property and zoom-and-property functions, when a feature does not contain a value for the specified property.\n* In identity functions, when the feature value is not valid for the style property (for example, if the function is being used for a `circle-color` property but the feature property value is not a string or not a valid color).\n* In interval or exponential property and zoom-and-property functions, when the feature value is not numeric.\nIf no default is provided, the style property's default is used in these circumstances."
    }
  },
  "function_stop": {
    "type": "array",
    "minimum": 0,
    "maximum": 24,
    "value": [
      "number",
      "color"
    ],
    "length": 2,
    "doc": "Zoom level and value pair."
  },
  "expression": {
    "type": "array",
    "value": "*",
    "minimum": 1,
    "doc": "An expression defines a function that can be used for data-driven style properties or feature filters."
  },
  "expression_name": {
    "doc": "",
    "type": "enum",
    "values": {
      "let": {
        "doc": "Binds expressions to named variables, which can then be referenced in the result expression using [\"var\", \"variable_name\"].",
        "group": "Variable binding",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "var": {
        "doc": "References variable bound using \"let\".",
        "group": "Variable binding",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "config": {
        "doc": "Retrieves the configuration value for the given option. Returns null if the requested option is missing.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "3.0.0",
            "android": "11.0.0",
            "ios": "11.0.0"
          }
        }
      },
      "literal": {
        "doc": "Provides a literal array or object value.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "array": {
        "doc": "Asserts that the input is an array (optionally with a specific item type and length). If, when the input expression is evaluated, it is not of the asserted type, then this assertion will cause the whole expression to be aborted.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "at": {
        "doc": "Retrieves an item from an array.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "at-interpolated": {
        "doc": "Retrieves an item from an array. If the array contains numeric values and the provided index is non-integer, the expression returns an interpolated value between adjacent items.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "3.11.0",
            "android": "11.12.0",
            "ios": "11.12.0"
          }
        }
      },
      "in": {
        "doc": "Determines whether an item exists in an array or a substring exists in a string. In the specific case when the second and third arguments are string literals, you must wrap at least one of them in a [`literal`](#types-literal) expression to hint correct interpretation to the [type system](#type-system).",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "1.6.0",
            "android": "9.1.0",
            "ios": "5.8.0"
          }
        }
      },
      "index-of": {
        "doc": "Returns the first position at which an item can be found in an array or a substring can be found in a string, or `-1` if the input cannot be found. Accepts an optional index from where to begin the search.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "1.10.0",
            "android": "10.0.0",
            "ios": "10.0.0"
          }
        }
      },
      "slice": {
        "doc": "Returns an item from an array or a substring from a string from a specified start index, or between a start index and an end index if set. The return value is inclusive of the start index but not of the end index.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "1.10.0",
            "android": "10.0.0",
            "ios": "10.0.0"
          }
        }
      },
      "split": {
        "doc": "Returns an array of substrings from a string, split by a delimiter parameter.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "3.14.0",
            "android": "11.14.0",
            "ios": "11.14.0"
          }
        }
      },
      "case": {
        "doc": "Selects the first output whose corresponding test condition evaluates to true, or the fallback value otherwise.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "match": {
        "doc": "Selects the output for which the label value matches the input value, or the fallback value if no match is found. The input can be any expression (for example, `[\"get\", \"building_type\"]`). Each label must be unique, and must be either:\n - a single literal value; or\n - an array of literal values, the values of which must be all strings or all numbers (for example `[100, 101]` or `[\"c\", \"b\"]`).\n\nThe input matches if any of the values in the array matches using strict equality, similar to the `\"in\"` operator.\nIf the input type does not match the type of the labels, the result will be the fallback value.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "coalesce": {
        "doc": "Evaluates each expression in turn until the first valid value is obtained. Invalid values are `null` and [`'image'`](#types-image) expressions that are unavailable in the style. If all values are invalid, `coalesce` returns the first value listed.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "step": {
        "doc": "Produces discrete, stepped results by evaluating a piecewise-constant function defined by pairs of input and output values (\"stops\"). The `input` may be any numeric expression (e.g., `[\"get\", \"population\"]`). Stop inputs must be numeric literals in strictly ascending order. Returns the output value of the stop just less than the input, or the first output if the input is less than the first stop.",
        "group": "Ramps, scales, curves",
        "sdk-support": {
          "basic functionality": {
            "js": "0.42.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "interpolate": {
        "doc": "Produces continuous, smooth results by interpolating between pairs of input and output values (\"stops\"). The `input` may be any numeric expression (e.g., `[\"get\", \"population\"]`). Stop inputs must be numeric literals in strictly ascending order. The output type must be `number`, `array<number>`, or `color`.\n\nInterpolation types:\n- `[\"linear\"]`: Interpolates linearly between the pair of stops just less than and just greater than the input.\n- `[\"exponential\", base]`: Interpolates exponentially between the stops just less than and just greater than the input. `base` controls the rate at which the output increases: higher values make the output increase more towards the high end of the range. With values close to 1 the output increases linearly.\n- `[\"cubic-bezier\", x1, y1, x2, y2]`: Interpolates using the cubic bezier curve defined by the given control points.",
        "group": "Ramps, scales, curves",
        "sdk-support": {
          "basic functionality": {
            "js": "0.42.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "interpolate-hcl": {
        "doc": "Produces continuous, smooth results by interpolating between pairs of input and output values (\"stops\"). Works like `interpolate`, but the output type must be `color`, and the interpolation is performed in the Hue-Chroma-Luminance color space.",
        "group": "Ramps, scales, curves",
        "sdk-support": {
          "basic functionality": {
            "js": "0.49.0"
          }
        }
      },
      "interpolate-lab": {
        "doc": "Produces continuous, smooth results by interpolating between pairs of input and output values (\"stops\"). Works like `interpolate`, but the output type must be `color`, and the interpolation is performed in the CIELAB color space.",
        "group": "Ramps, scales, curves",
        "sdk-support": {
          "basic functionality": {
            "js": "0.49.0"
          }
        }
      },
      "ln2": {
        "doc": "Returns mathematical constant ln(2).",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "pi": {
        "doc": "Returns the mathematical constant pi.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "e": {
        "doc": "Returns the mathematical constant e.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "typeof": {
        "doc": "Returns a string describing the type of the given value.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "string": {
        "doc": "Asserts that the input value is a string. If multiple values are provided, each one is evaluated in order until a string is obtained. If none of the inputs are strings, the expression is an error.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "number": {
        "doc": "Asserts that the input value is a number. If multiple values are provided, each one is evaluated in order until a number is obtained. If none of the inputs are numbers, the expression is an error.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "boolean": {
        "doc": "Asserts that the input value is a boolean. If multiple values are provided, each one is evaluated in order until a boolean is obtained. If none of the inputs are booleans, the expression is an error.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "object": {
        "doc": "Asserts that the input value is an object. If multiple values are provided, each one is evaluated in order until an object is obtained. If none of the inputs are objects, the expression is an error.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "collator": {
        "doc": "Returns a `collator` for use in locale-dependent comparison operations. The `case-sensitive` and `diacritic-sensitive` options default to `false`. The `locale` argument specifies the IETF language tag of the locale to use. If none is provided, the default locale is used. If the requested locale is not available, the `collator` will use a system-defined fallback locale. Use `resolved-locale` to test the results of locale fallback behavior.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.2.0"
          }
        }
      },
      "format": {
        "doc": "Returns a `formatted` string for displaying mixed-format text in the `text-field` property. The input may contain a string literal or expression, including an [`'image'`](#types-image) expression. Strings may be followed by a style override object that supports the following properties:\n- `\"text-font\"`: Overrides the font stack specified by the root layout property.\n- `\"text-color\"`: Overrides the color specified by the root paint property.\n- `\"font-scale\"`: Applies a scaling factor on `text-size` as specified by the root layout property.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.48.0",
            "android": "6.7.0",
            "ios": "4.6.0"
          },
          "text-font": {
            "js": "0.48.0",
            "android": "6.7.0",
            "ios": "4.6.0"
          },
          "font-scale": {
            "js": "0.48.0",
            "android": "6.7.0",
            "ios": "4.6.0"
          },
          "text-color": {
            "js": "1.3.0",
            "android": "7.3.0",
            "ios": "4.10.0"
          },
          "image": {
            "js": "1.6.0",
            "android": "8.6.0",
            "ios": "5.7.0"
          }
        }
      },
      "image": {
        "doc": "Returns a [`ResolvedImage`](/style-spec/reference/types/#resolvedimage) for use in [`icon-image`](/style-spec/reference/layers/#layout-symbol-icon-image), `*-pattern` entries, and as a section in the [`'format'`](#types-format) expression.\n\nA [`'coalesce'`](#coalesce) expression containing `image` expressions will evaluate to the first listed image that is currently in the style. This validation process is synchronous and requires the image to have been added to the style before requesting it in the `'image'` argument.\n\nEvery image name can be followed by an optional [`ImageOptions`](/style-spec/reference/types/#imageoptions) object, which will be used for vector images only.\n\nTo implement crossfading between two images within a symbol layer using the [`icon-image-cross-fade`](/style-spec/reference/layers/#paint-symbol-icon-image-cross-fade) attribute, include a second image as the second argument in the `'image'` expression.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "1.4.0",
            "android": "8.6.0",
            "ios": "5.7.0"
          }
        }
      },
      "number-format": {
        "doc": "Converts the input number into a string representation using the providing formatting rules. If set, the `locale` argument specifies the locale to use, as a BCP 47 language tag. If set, the `currency` argument specifies an ISO 4217 code to use for currency-style formatting. If set, the `unit` argument specifies a [simple ECMAScript unit](https://tc39.es/proposal-unified-intl-numberformat/section6/locales-currencies-tz_proposed_out.html#sec-issanctionedsimpleunitidentifier) to use for unit-style formatting. If set, the `min-fraction-digits` and `max-fraction-digits` arguments specify the minimum and maximum number of fractional digits to include.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.54.0",
            "android" : "8.4.0",
            "ios": "5.4.0"
          }
        }
      },
      "to-string": {
        "doc": "Converts the input value to a string. If the input is `null`, the result is `\"\"`. If the input is a [`boolean`](#types-boolean), the result is `\"true\"` or `\"false\"`. If the input is a number, it is converted to a string as specified by the [\"NumberToString\" algorithm](https://tc39.github.io/ecma262/#sec-tostring-applied-to-the-number-type) of the ECMAScript Language Specification. If the input is a [`color`](#color), it is converted to a string of the form `\"rgba(r,g,b,a)\"`, where `r`, `g`, and `b` are numerals ranging from 0 to 255, and `a` ranges from 0 to 1. If the input is an [`'image'`](#types-image) expression, `'to-string'` returns the image name. Otherwise, the input is converted to a string in the format specified by the [`JSON.stringify`](https://tc39.github.io/ecma262/#sec-json.stringify) function of the ECMAScript Language Specification.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "to-number": {
        "doc": "Converts the input value to a number, if possible. If the input is `null` or `false`, the result is 0. If the input is `true`, the result is 1. If the input is a string, it is converted to a number as specified by the [\"ToNumber Applied to the String Type\" algorithm](https://tc39.github.io/ecma262/#sec-tonumber-applied-to-the-string-type) of the ECMAScript Language Specification. If multiple values are provided, each one is evaluated in order until the first successful conversion is obtained. If none of the inputs can be converted, the expression is an error.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "to-boolean": {
        "doc": "Converts the input value to a boolean. The result is `false` when then input is an empty string, 0, `false`, `null`, or `NaN`; otherwise it is `true`.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "to-rgba": {
        "doc": "Returns a four-element array containing the input color's red, green, blue, and alpha components, in that order.",
        "group": "Color",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "to-hsla": {
        "doc": "Returns a four-element array containing the input color's Hue, Saturation, Luminance and alpha components, in that order.",
        "group": "Color",
        "sdk-support": {
          "basic functionality": {
            "js": "3.9.0",
            "android": "11.9.0",
            "ios": "11.9.0"
          }
        }
      },
      "to-color": {
        "doc": "Converts the input value to a color. If multiple values are provided, each one is evaluated in order until the first successful conversion is obtained. If none of the inputs can be converted, the expression is an error.",
        "group": "Types",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "rgb": {
        "doc": "Creates a color value from red, green, and blue components, which must range between 0 and 255, and an alpha component of 1. If any component is out of range, the expression is an error.",
        "group": "Color",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "rgba": {
        "doc": "Creates a color value from red, green, blue components, which must range between 0 and 255, and an alpha component which must range between 0 and 1. If any component is out of range, the expression is an error.",
        "group": "Color",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "hsl": {
        "doc": "Creates a color value from hue (range 0-360), saturation and lightness components (range 0-100), and an alpha component of 1. If any component is out of range, the expression is an error.",
        "group": "Color",
        "sdk-support": {
          "basic functionality": {
            "js": "2.12.1",
            "android": "10.11.0",
            "ios": "10.11.0"
          }
        }
      },
      "hsla": {
        "doc": "Creates a color value from hue (range 0-360), saturation and lightness components (range 0-100), and an alpha component (range 0-1). If any component is out of range, the expression is an error.",
        "group": "Color",
        "sdk-support": {
          "basic functionality": {
            "js": "2.12.1",
            "android": "10.11.0",
            "ios": "10.11.0"
          }
        }
      },
      "get": {
        "doc": "Retrieves a property value from the current feature's properties, or from another object if a second argument is provided. Returns `null` if the requested property is missing.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "has": {
        "doc": "Tests for the presence of an property value in the current feature's properties, or from another object if a second argument is provided.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "length": {
        "doc": "Returns the length of an array or string.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "properties": {
        "doc": "Returns the feature properties object. Note that in some cases, it may be more efficient to use `[\"get\", \"property_name\"]` directly.",
        "group": "Feature data",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "feature-state": {
        "doc": "Retrieves a property value from the current feature's state. Returns `null` if the requested property is not present on the feature's state. A feature's state is not part of the GeoJSON or vector tile data, and must be set programmatically on each feature. Features are identified by their `id` attribute, which must be an integer or a string that can be cast to an integer. Note that [\"feature-state\"] can only be used with paint properties that support data-driven styling.",
        "group": "Feature data",
        "sdk-support": {
          "basic functionality": {
            "js": "0.46.0",
            "android": "10.0.0",
            "ios": "10.0.0"
          }
        }
      },
      "geometry-type": {
        "doc": "Returns the feature's geometry type: `Point`, `LineString` or `Polygon`. `Multi*` feature types return the singular forms.",
        "group": "Feature data",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "worldview": {
        "doc": "Returns the current worldview being used.",
        "group": "Lookup",
        "experimental": true,
        "sdk-support": {
          "basic functionality": {
            "js": "3.13.0",
	          "android": "11.13.0",
	          "ios": "11.13.0"
          }
        }
      },
      "id": {
        "doc": "Returns the feature's id, if it has one.",
        "group": "Feature data",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "zoom": {
        "doc": "Returns the current zoom level. Note that in style layout and paint properties, [\"zoom\"] may only appear as the input to a top-level \"step\" or \"interpolate\" expression.",
        "group": "Camera",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "pitch": {
        "doc": "Returns the current pitch in degrees. `[\"pitch\"]` may only be used in the `filter` expression for a `symbol` layer.",
        "group": "Camera",
        "sdk-support": {
          "basic functionality": {
            "js": "2.6.0",
            "android": "10.9.0",
            "ios": "10.9.0"
          }
        }
      },
      "distance-from-center": {
        "doc": "Returns the distance of a `symbol` instance from the center of the map. The distance is measured in pixels divided by the height of the map container. It measures 0 at the center, decreases towards the camera and increase away from the camera. For example, if the height of the map is 1000px, a value of -1 means 1000px away from the center towards the camera, and a value of 1 means a distance of 1000px away from the camera from the center. `[\"distance-from-center\"]` may only be used in the `filter` expression for a `symbol` layer.",
        "group": "Camera",
        "sdk-support": {
          "basic functionality": {
            "js": "2.6.0",
            "android": "10.9.0",
            "ios": "10.9.0"
          }
        }
      },
      "measure-light": {
        "doc": "Returns a requested property of the light configuration based on the supplied options. Currently the only supported option is `brightness` which returns the global brightness value of the lights on a scale of 0 to 1, where 0 means total darkness and 1 means full brightness. This expression works only with 3D light, i.e. when `lights` root property is defined.",
        "group": "Lookup",
        "sdk-support": {
          "basic functionality": {
            "js": "3.0.0",
            "android": "11.0.0",
            "ios": "11.0.0"
          }
        }
      },
      "heatmap-density": {
        "doc": "Returns the kernel density estimation of a pixel in a heatmap layer, which is a relative measure of how many data points are crowded around a particular pixel. Can only be used in the `heatmap-color` property.",
        "group": "Heatmap",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "line-progress": {
        "doc": "Returns the progress along a gradient line. Can only be used in the `line-gradient` and `line-z-offset` properties.",
        "group": "Feature data",
        "sdk-support": {
          "basic functionality": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.6.0"
          }
        }
      },
      "sky-radial-progress": {
        "doc": "Returns the distance of a point on the sky from the sun position. Returns 0 at sun position and 1 when the distance reaches `sky-gradient-radius`. Can only be used in the `sky-gradient` property.",
        "group": "sky",
        "sdk-support": {
          "basic functionality": {
            "js": "2.0.0",
            "ios": "10.0.0",
            "android": "10.0.0"
          }
        }
      },
      "accumulated": {
        "doc": "Returns the value of a cluster property accumulated so far. Can only be used in the `clusterProperties` option of a clustered GeoJSON source.",
        "group": "Feature data",
        "sdk-support": {
          "basic functionality": {
            "js": "0.53.0",
            "android": "8.4.0",
            "ios": "5.5.0"
          }
        }
      },
      "+": {
        "doc": "Returns the sum of the inputs.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "*": {
        "doc": "Returns the product of the inputs.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "-": {
        "doc": "For two inputs, returns the result of subtracting the second input from the first. For a single input, returns the result of subtracting it from 0.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "/": {
        "doc": "Returns the result of floating point division of the first input by the second.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "%": {
        "doc": "Returns the remainder after integer division of the first input by the second.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "^": {
        "doc": "Returns the result of raising the first input to the power specified by the second.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "sqrt": {
        "doc": "Returns the square root of the input.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.42.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "log10": {
        "doc": "Returns the base-ten logarithm of the input.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "ln": {
        "doc": "Returns the natural logarithm of the input.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "log2": {
        "doc": "Returns the base-two logarithm of the input.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "sin": {
        "doc": "Returns the sine of the input, interpreted as radians.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "cos": {
        "doc": "Returns the cosine of the input, interpreted as radians.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "tan": {
        "doc": "Returns the tangent of the input, interpreted as radians.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "asin": {
        "doc": "Returns the arcsine of the input, in radians between −π/2 and π/2.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "acos": {
        "doc": "Returns the arccosine of the input, in radians between −π/2 and π/2.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "atan": {
        "doc": "Returns the arctangent of the input, in radians between −π/2 and π/2.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "min": {
        "doc": "Returns the minimum value of the inputs.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "max": {
        "doc": "Returns the maximum value of the inputs.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "round": {
        "doc": "Rounds the input to the nearest integer. Halfway values are rounded away from zero. For example, `[\"round\", -1.5]` evaluates to -2.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.45.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "abs": {
        "doc": "Returns the absolute value of the input.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.45.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "ceil": {
        "doc": "Returns the smallest integer that is greater than or equal to the input.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.45.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "floor": {
        "doc": "Returns the largest integer that is less than or equal to the input.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "0.45.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "distance": {
        "doc": "Returns the shortest distance in meters between the evaluated feature and the input geometry. The input value can be a valid GeoJSON of type `Point`, `MultiPoint`, `LineString`, `MultiLineString`, `Polygon`, `MultiPolygon`, `Feature`, or `FeatureCollection`, or expression that returns a valid GeoJSON. Distance values returned may vary in precision due to loss in precision from encoding geometries, particularly below zoom level 13.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "3.0.0",
            "android": "9.2.0",
            "ios": "5.9.0"
          },
          "expressions support": {
            "js": "3.29.0",
            "android": "11.29.0",
            "ios": "11.29.0"
          }
        }
      },
      "==": {
        "doc": "Returns `true` if the input values are equal, `false` otherwise. The comparison is strictly typed: values of different runtime types are always considered unequal. Cases where the types are known to be different at parse time are considered invalid and will produce a parse error. Accepts an optional `collator` argument to control locale-dependent string comparisons.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          },
          "collator": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.2.0"
          }
        }
      },
      "!=": {
        "doc": "Returns `true` if the input values are not equal, `false` otherwise. The comparison is strictly typed: values of different runtime types are always considered unequal. Cases where the types are known to be different at parse time are considered invalid and will produce a parse error. Accepts an optional `collator` argument to control locale-dependent string comparisons.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          },
          "collator": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.2.0"
          }
        }
      },
      ">": {
        "doc": "Returns `true` if the first input is strictly greater than the second, `false` otherwise. The arguments are required to be either both strings or both numbers; if during evaluation they are not, expression evaluation produces an error. Cases where this constraint is known not to hold at parse time are considered in valid and will produce a parse error. Accepts an optional `collator` argument to control locale-dependent string comparisons.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          },
          "collator": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.2.0"
          }
        }
      },
      "<": {
        "doc": "Returns `true` if the first input is strictly less than the second, `false` otherwise. The arguments are required to be either both strings or both numbers; if during evaluation they are not, expression evaluation produces an error. Cases where this constraint is known not to hold at parse time are considered in valid and will produce a parse error. Accepts an optional `collator` argument to control locale-dependent string comparisons.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          },
          "collator": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.2.0"
          }
        }
      },
      ">=": {
        "doc": "Returns `true` if the first input is greater than or equal to the second, `false` otherwise. The arguments are required to be either both strings or both numbers; if during evaluation they are not, expression evaluation produces an error. Cases where this constraint is known not to hold at parse time are considered in valid and will produce a parse error. Accepts an optional `collator` argument to control locale-dependent string comparisons.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          },
          "collator": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.2.0"
          }
        }
      },
      "<=": {
        "doc": "Returns `true` if the first input is less than or equal to the second, `false` otherwise. The arguments are required to be either both strings or both numbers; if during evaluation they are not, expression evaluation produces an error. Cases where this constraint is known not to hold at parse time are considered in valid and will produce a parse error. Accepts an optional `collator` argument to control locale-dependent string comparisons.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          },
          "collator": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.2.0"
          }
        }
      },
      "all": {
        "doc": "Returns `true` if all the inputs are `true`, `false` otherwise. The inputs are evaluated in order, and evaluation is short-circuiting: once an input expression evaluates to `false`, the result is `false` and no further input expressions are evaluated.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "any": {
        "doc": "Returns `true` if any of the inputs are `true`, `false` otherwise. The inputs are evaluated in order, and evaluation is short-circuiting: once an input expression evaluates to `true`, the result is `true` and no further input expressions are evaluated.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "!": {
        "doc": "Logical negation. Returns `true` if the input is `false`, and `false` if the input is `true`.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "within": {
        "doc": "Returns `true` if the evaluated feature is fully contained inside a boundary of the input geometry, `false` otherwise. The input value can be a valid GeoJSON of type `Polygon`, `MultiPolygon`, `Feature`, or `FeatureCollection`. Supported features for evaluation:\n- `Point`: Returns `false` if a point is on the boundary or falls outside the boundary.\n- `LineString`: Returns `false` if any part of a line falls outside the boundary, the line intersects the boundary, or a line's endpoint is on the boundary.",
        "group": "Decision",
        "sdk-support": {
          "basic functionality": {
            "js": "1.9.0",
            "android": "9.1.0",
            "ios": "5.8.0"
          }
        }
      },
      "is-supported-script": {
        "doc": "Returns `true` if the input string is expected to render legibly. Returns `false` if the input string contains sections that cannot be rendered without potential loss of meaning (e.g. Indic scripts that require complex text shaping, or right-to-left scripts if the the `mapbox-gl-rtl-text` plugin is not in use in Mapbox GL JS).",
        "group": "String",
        "sdk-support": {
          "basic functionality": {
            "js": "0.45.0",
            "android": "6.6.0",
            "ios": "4.1.0"
          }
        }
      },
      "upcase": {
        "doc": "Returns the input string converted to uppercase. Follows the Unicode Default Case Conversion algorithm and the locale-insensitive case mappings in the Unicode Character Database.",
        "group": "String",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "downcase": {
        "doc": "Returns the input string converted to lowercase. Follows the Unicode Default Case Conversion algorithm and the locale-insensitive case mappings in the Unicode Character Database.",
        "group": "String",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "concat": {
        "doc": "Returns a `string` consisting of the concatenation of the inputs. Each input is converted to a string as if by `to-string`.",
        "group": "String",
        "sdk-support": {
          "basic functionality": {
            "js": "0.41.0",
            "android": "6.0.0",
            "ios": "4.0.0"
          }
        }
      },
      "resolved-locale": {
        "doc": "Returns the IETF language tag of the locale being used by the provided `collator`. This can be used to determine the default system locale, or to determine if a requested locale was successfully loaded.",
        "group": "String",
        "sdk-support": {
          "basic functionality": {
            "js": "0.45.0",
            "android": "6.5.0",
            "ios": "4.2.0"
          }
        }
      },
      "raster-value": {
        "doc": "Returns the raster value of a pixel computed via `raster-color-mix`. Can only be used in the `raster-color` property.",
        "group": "Raster Colorization",
        "sdk-support": {
          "basic functionality": {
            "js": "3.0.0",
            "android": "11.0.0",
            "ios": "11.0.0"
          }
        }
      },
      "raster-particle-speed": {
        "doc": "Returns the length of the particle velocity vector. Can only be used in the `raster-particle-color` property.",
        "group": "Raster Particle Animation",
        "sdk-support": {
          "basic functionality": {
            "js": "3.3.0",
            "android": "11.4.0",
            "ios": "11.4.0"
          }
        }
      },
      "is-active-floor": {
        "doc": "Experimental. Returns `true` if the input floor id belongs to one of the active indoor floors, `false` otherwise. In case of array of strings, returns `true` if any of the input floor ids belongs to one of the active indoor floors, `false` otherwise. Only supported in filters.",
        "group": "Indoor",
        "experimental": true,
        "private": true,
        "sdk-support": {
          "basic functionality": {
            "js": "3.16.0",
            "android": "11.16.0",
            "ios": "11.16.0"
          }
        }
      },
      "random": {
        "doc": "Returns a random value in the specified range (first two input numbers) based on a supplied seed (third input). The seed can be an expression or a constant number or string value.",
        "group": "Math",
        "sdk-support": {
          "basic functionality": {
            "js": "3.0.0",
            "android": "11.0.0",
            "ios": "11.0.0"
          }
        }
      }
    }
  },
  "fog": {
    "range": {
      "type": "array",
      "default": [
        0.5,
        10
      ],
      "minimum": -20,
      "maximum": 20,
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "doc": "The start and end distance range in which fog fades from fully transparent to fully opaque. The distance to the point at the center of the map is defined as zero, so that negative range values are closer to the camera, and positive values are farther away.",
      "example": [
        0.5,
        10
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "2.3.0",
          "android": "10.6.0",
          "ios": "10.6.0"
        }
      }
    },
    "color": {
      "type": "color",
      "property-type": "data-constant",
      "default": "#ffffff",
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "use-theme": true,
      "transition": true,
      "doc": "The color of the atmosphere region immediately below the horizon and within the `range` and above the horizon and within `horizon-blend`. Using opacity is recommended only for smoothly transitioning fog on/off as anything less than 100% opacity results in more tiles loaded and drawn.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.3.0",
          "android": "10.6.0",
          "ios": "10.6.0"
        }
      }
    },
    "high-color": {
      "type": "color",
      "property-type": "data-constant",
      "default": "#245cdf",
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "use-theme": true,
      "transition": true,
      "doc": "The color of the atmosphere region above the horizon, `high-color` extends further above the horizon than the `color` property and its spread can be controlled with `horizon-blend`. The opacity can be set to `0` to remove the high atmosphere color contribution.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.9.0",
          "android": "10.6.0",
          "ios": "10.6.0"
        }
      }
    },
    "space-color": {
      "type": "color",
      "property-type": "data-constant",
      "default":
      [
        "interpolate",
        [
          "linear"
        ],
        [
          "zoom"
        ],
        4,
        "#010b19",
        7,
        "#367ab9"
      ],
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "use-theme": true,
      "transition": true,
      "doc": "The color of the region above the horizon and after the end of the `horizon-blend` contribution. The opacity can be set to `0` to have a transparent background.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.9.0",
          "android": "10.6.0",
          "ios": "10.6.0"
        }
      }
    },
    "horizon-blend": {
      "type": "number",
      "property-type": "data-constant",
      "default":
      [
        "interpolate",
        [
          "linear"
        ],
        [
          "zoom"
        ],
        4,
        0.2,
        7,
        0.1
      ],
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Horizon blend applies a smooth fade from the color of the atmosphere to the color of space. A value of zero leaves a sharp transition from atmosphere to space. Increasing the value blends the color of atmosphere into increasingly high angles of the sky.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.3.0",
          "android": "10.6.0",
          "ios": "10.6.0"
        }
      }
    },
    "star-intensity": {
      "type": "number",
      "property-type": "data-constant",
      "default":
      [
        "interpolate",
        [
          "linear"
        ],
        [
          "zoom"
        ],
        5,
        0.35,
        6,
        0
      ],
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "A value controlling the star intensity where `0` will show no stars and `1` will show stars at their maximum intensity.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.9.0",
          "android": "10.6.0",
          "ios": "10.6.0"
        }
      }
    },
    "vertical-range": {
      "type": "array",
      "default": [0,0],
      "minimum": 0,
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "doc": "An array of two number values, specifying the vertical range, measured in meters, over which the fog should gradually fade out. When both parameters are set to zero, the fog will be rendered without any vertical constraints.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    }
  },
  "snow": {
    "density": {
      "type": "number",
      "property-type": "data-constant",
      "default": [
        "interpolate",
        ["linear"],
        ["zoom"],
        11,
        0.0,
        13,
        0.85
      ],
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Snow particles density. Controls the overall particles number.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "intensity": {
      "type": "number",
      "property-type": "data-constant",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Snow particles movement factor. Controls the overall particles movement speed.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "color": {
      "type": "color",
      "property-type": "data-constant",
      "default": "#ffffff",
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "use-theme": true,
      "transition": true,
      "doc": "Snow particles color.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "opacity": {
      "type": "number",
      "property-type": "data-constant",
      "default": 1.0,
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Snow particles opacity.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "vignette": {
      "type": "number",
      "property-type": "data-constant",
      "default": [
        "interpolate",
        ["linear"],
        ["zoom"],
        11,
        0.0,
        13,
        0.3
      ],
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Snow vignette screen-space effect. Adds snow tint to screen corners",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "vignette-color": {
      "type": "color",
      "property-type": "data-constant",
      "default": "#ffffff",
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "use-theme": true,
      "transition": true,
      "doc": "Snow vignette screen-space corners tint color.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "center-thinning": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.4,
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Thinning factor of snow particles from center. 0 - no thinning. 1 - maximal central area thinning.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "direction": {
      "type": "array",
      "default": [
        0.0,
        50.0
      ],
      "minimum": 0,
      "maximum": 360,
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "doc": "Main snow particles direction. Azimuth and polar angles",
      "example": [
        0,
        45
      ],
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "flake-size": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.71,
      "minimum": 0,
      "maximum": 5,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Snow flake particle size. Correlates with individual particle screen size",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    }
  },
  "rain": {
    "density": {
      "type": "number",
      "property-type": "data-constant",
      "default": [
        "interpolate",
        ["linear"],
        ["zoom"],
        11,
        0.0,
        13,
        0.5
      ],
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Rain particles density. Controls the overall screen density of the rain.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "intensity": {
      "type": "number",
      "property-type": "data-constant",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Rain particles movement factor. Controls the overall rain particles speed",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "color": {
      "type": "color",
      "property-type": "data-constant",
      "default":  [
        "interpolate",
        ["linear"],
        [ "measure-light", "brightness" ],
        0,
        "#03113d",
        0.3,
        "#a8adbc"
      ],
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "use-theme": true,
      "transition": true,
      "doc": "Individual rain particle dorplets color.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "opacity": {
      "type": "number",
      "property-type": "data-constant",
      "default": [
        "interpolate",
        ["linear"],
        [ "measure-light", "brightness" ],
        0,
        0.88,
        1,
        0.7
      ],
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Rain particles opacity.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "vignette": {
      "type": "number",
      "property-type": "data-constant",
      "default": [
        "interpolate",
        ["linear"],
        ["zoom"],
        11,
        0.0,
        13,
        1.0
      ],
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Screen-space vignette rain tinting effect intensity.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "vignette-color": {
      "type": "color",
      "property-type": "data-constant",
      "default": [
        "interpolate",
        ["linear"],
        [ "measure-light", "brightness" ],
        0,
        "#001736",
        0.3,
        "#464646"
      ],
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "use-theme": true,
      "transition": true,
      "doc": "Rain vignette screen-space corners tint color.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "center-thinning": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.57,
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Thinning factor of rain particles from center. 0 - no thinning. 1 - maximal central area thinning.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "direction": {
      "type": "array",
      "default": [
        0.0,
        80.0
      ],
      "minimum": 0,
      "maximum": 360,
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "doc": "Main rain particles direction. Azimuth and polar angles.",
      "example": [
        0,
        45
      ],
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "droplet-size": {
      "type": "array",
      "default": [
        2.6,
        18.2
      ],
      "minimum": 0,
      "maximum": 50,
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "doc": "Rain droplet size. x - normal to direction, y - along direction",
      "example": [
        0,
        45
      ],
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    },
    "distortion-strength": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.7,
      "minimum": 0,
      "maximum": 1,
      "experimental": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ],
        "relaxZoomRestriction": true
      },
      "transition": true,
      "doc": "Rain particles screen-space distortion strength.",
      "sdk-support": {
        "basic functionality": {
          "android": "11.9.0",
          "ios": "11.9.0",
          "js": "3.9.0"
        }
      }
    }
  },
  "camera": {
    "camera-projection" : {
      "doc": "Camera projection describes how 3D world geometry get projected into 2D screen",
      "type": "enum",
      "values": {
        "perspective": {
          "doc": "linear projection where distant objects appear smaller than closer objects. Lines that are parallel seem to converge towards a vanishing point"
        },
        "orthographic": {
          "doc": "Projection where objects are of the same scale regardless of whether they are far away or near to the camera. Parallel lines remains parallel and there is no vanishing point."
        }
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "default": "perspective",
      "property-type": "data-constant"
    }
  },
  "colorTheme": {
    "data": {
      "type": "string",
      "doc": "Expects a base64 encoded PNG image which represents a cube strip LUT. The height of the image cannot exceed 32 pixels and the width must be equal to the height squared.",
      "transition": false,
      "property-type": "data-constant",
      "expression": {
        "interpolated": false
      }
    }
  },
  "indoor_source": {
    "sourceId": {
      "type": "string",
      "doc": "Source ID of a source to be used to retrieve indoor data.",
      "experimental": true,
      "private": true,
      "transition": false,
      "property-type": "data-constant"
    },
    "sourceLayers": {
      "type": "array",
      "value": "string",
      "doc": "An array of source layers to be used to retrieve indoor data.",
      "experimental": true,
      "private": true,
      "transition": false,
      "property-type": "data-constant"
    }
  },
  "indoor": {
    "*": {
      "type": "indoor_source",
      "private": true,
      "experimental": true,
      "doc": "Specification of an indoor source - sourceId and sourceLayer required for vector source, for GeoJSON sourceLayers should be omitted."
    }
  },
  "light": {
    "anchor": {
      "type": "enum",
      "default": "viewport",
      "values": {
        "map": {
          "doc": "The position of the light source is aligned to the rotation of the map."
        },
        "viewport": {
          "doc": "The position of the light source is aligned to the rotation of the viewport."
        }
      },
      "property-type": "data-constant",
      "transition": false,
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "doc": "Whether extruded geometries are lit relative to the map or viewport.",
      "example": "map",
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      }
    },
    "position": {
      "type": "array",
      "default": [
        1.15,
        210,
        30
      ],
      "length": 3,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "doc": "Position of the light source relative to lit (extruded) geometries, in [r radial coordinate, a azimuthal angle, p polar angle] where r indicates the distance from the center of the base of an object to its light, a indicates the position of the light relative to 0° (0° when `light.anchor` is set to `viewport` corresponds to the top of the viewport, or 0° when `light.anchor` is set to `map` corresponds to due north, and degrees proceed clockwise), and p indicates the height of the light (from 0°, directly above, to 180°, directly below).",
      "example": [
        1.5,
        90,
        80
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      }
    },
    "color": {
      "type": "color",
      "property-type": "data-constant",
      "default": "#ffffff",
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "use-theme": true,
      "transition": true,
      "doc": "Color tint for lighting extruded geometries.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      }
    },
    "intensity": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.5,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "Intensity of lighting (on a scale from 0 to 1). Higher numbers will present as more extreme contrast.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      }
    }
  },
  "projection": {
    "name": {
      "type": "enum",
      "values": {
        "albers": {
          "doc": "An Albers equal-area projection centered on the continental United States. You can configure the projection for a different region by setting `center` and `parallels` properties. You may want to set max bounds to constrain the map to the relevant region."
        },
        "equalEarth": {
          "doc": "An Equal Earth projection."
        },
        "equirectangular": {
          "doc": "An Equirectangular projection. This projection is very similar to the Plate Carrée projection."
        },
        "lambertConformalConic": {
          "doc": "A Lambert conformal conic projection. You can configure the projection for a region by setting `center` and `parallels` properties. You may want to set max bounds to constrain the map to the relevant region."
        },
        "mercator": {
          "doc": "The Mercator projection is the default projection."
        },
        "naturalEarth": {
          "doc": "A Natural Earth projection."
        },
        "winkelTripel": {
          "doc": "A Winkel Tripel projection."
        },
        "globe": {
          "doc": "A globe projection."
        }
      },
      "default": "mercator",
      "doc": "The name of the projection to be used for rendering the map.",
      "required": true,
      "sdk-support": {
        "basic functionality": {
          "js": "2.6.0",
          "ios": "10.5.0",
          "android": "10.5.0"
        }
      }
    },
    "center": {
      "type": "array",
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "minimum": [-180, -90],
      "maximum": [180, 90],
      "transition": false,
      "doc": "The reference longitude and latitude of the projection. `center` takes the form of [lng, lat]. This property is only configurable for conic projections (Albers and Lambert Conformal Conic). All other projections are centered on [0, 0].",
      "example": [
        -96,
        37.5
      ],
      "requires": [
        {
          "name": [
              "albers",
              "lambertConformalConic"
          ]
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "2.6.0"
        }
      }
    },
    "parallels": {
      "type": "array",
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "minimum": [-90, -90],
      "maximum": [90, 90],
      "transition": false,
      "doc": "The standard parallels of the projection, denoting the desired latitude range with minimal distortion. `parallels` takes the form of [lat0, lat1]. This property is only configurable for conic projections (Albers and Lambert Conformal Conic).",
      "example": [
        29.5,
        45.5
      ],
      "requires": [
        {
          "name": [
              "albers",
              "lambertConformalConic"
          ]
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "2.6.0"
        }
      }
    }
  },
  "terrain" : {
    "source": {
      "type": "string",
      "doc": "Name of a source of `raster_dem` type to be used for terrain elevation.",
      "required": true,
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      }
    },
    "exaggeration": {
      "type": "number",
      "property-type": "data-constant",
      "default": 1.0,
      "minimum": 0,
      "maximum": 1000,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "Exaggerates the elevation of the terrain by multiplying the data from the DEM with this value.",
      "requires": [
        "source"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      }
    }
  },
  "paint": [
    "paint_fill",
    "paint_line",
    "paint_circle",
    "paint_heatmap",
    "paint_fill-extrusion",
    "paint_building",
    "paint_symbol",
    "paint_raster",
    "paint_raster-particle",
    "paint_hillshade",
    "paint_background",
    "paint_sky",
    "paint_model"
  ],
  "paint_fill": {
    "fill-antialias": {
      "type": "boolean",
      "default": true,
      "doc": "Whether or not the fill should be antialiased.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "fill-opacity": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "doc": "The opacity of the entire fill layer. In contrast to the `fill-color`, this value will also affect the 1px stroke around the fill, if the stroke is used.",
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.21.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-color": {
      "type": "color",
      "default": "#000000",
      "doc": "The color of the filled part of this layer. This color can be specified as `rgba` with an alpha component and the color's opacity will not affect the opacity of the 1px stroke, if it is used.",
      "use-theme": true,
      "transition": true,
      "requires": [
        {
          "!": "fill-pattern"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.19.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-outline-color": {
      "type": "color",
      "doc": "The outline color of the fill. Matches the value of `fill-color` if unspecified.",
      "use-theme": true,
      "transition": true,
      "requires": [
        {
          "!": "fill-pattern"
        },
        {
          "fill-antialias": true
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.19.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-translate": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0,
        0
      ],
      "transition": true,
      "units": "pixels",
      "doc": "The geometry's offset. Values are [x, y] where negatives indicate left and up, respectively.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "fill-translate-anchor": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The fill is translated relative to the map."
        },
        "viewport": {
          "doc": "The fill is translated relative to the viewport."
        }
      },
      "doc": "Controls the frame of reference for `fill-translate`.",
      "default": "map",
      "requires": [
        "fill-translate"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "fill-pattern": {
      "type": "resolvedImage",
      "transition": false,
      "doc": "Name of image in sprite to use for drawing image fills. For seamless patterns, image width and height must be a factor of two (2, 4, 8, ..., 512). Note that zoom-dependent expressions will be evaluated only at integer zoom levels.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.49.0",
          "android": "6.5.0",
          "ios": "4.4.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-pattern-cross-fade": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.0,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "requires": [
        "line-pattern"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.12.0",
          "android": "11.14.0",
          "ios": "11.14.0"
        }
      },
      "doc": "Controls the transition progress between the image variants of fill-pattern. Zero means the first variant is used, one is the second, and in between they are blended together. Both images should be the same size and have the same type (either raster or vector)."
    },
    "fill-emissive-strength": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    },
    "fill-z-offset": {
      "type": "number",
      "doc": "Specifies an uniform elevation in meters. Note: If the value is zero, the layer will be rendered on the ground. Non-zero values will elevate the layer from the sea level, which can cause it to be rendered below the terrain.",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "experimental": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.7.0"
        },
        "data-driven styling": {
          "js": "3.7.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-bridge-guard-rail-color": {
      "type": "color",
      "default": "rgba(241, 236, 225, 255)",
      "doc": "The color of bridge guard rail.",
      "experimental": true,
      "private": true,
      "use-theme": true,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "android": "11.11.0",
          "ios": "11.11.0",
          "js": "3.14.0"
        },
        "data-driven styling": {
          "android": "11.11.0",
          "ios": "11.11.0",
          "js": "3.14.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-tunnel-structure-color": {
      "type": "color",
      "default": "rgba(241, 236, 225, 255)",
      "doc": "The color of tunnel structures (tunnel entrance and tunnel walls).",
      "use-theme": true,
      "transition": true,
      "experimental": true,
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "android": "11.11.0",
          "ios": "11.11.0",
          "js": "3.14.0"
        },
        "data-driven styling": {
          "android": "11.11.0",
          "ios": "11.11.0",
          "js": "3.14.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light",
          "feature"
        ]
      },
      "property-type": "data-driven"
    }
  },
  "paint_fill-extrusion": {
    "fill-extrusion-opacity": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "doc": "The opacity of the entire fill extrusion layer. This is rendered on a per-layer, not per-feature, basis, and data-driven styling is not available.",
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "fill-extrusion-color": {
      "type": "color",
      "default": "#000000",
      "doc": "The base color of the extruded fill. The extrusion's surfaces will be shaded differently based on this color in combination with the root `light` settings. If this color is specified as `rgba` with an alpha component, the alpha component will be ignored; use `fill-extrusion-opacity` to set layer opacity.",
      "use-theme": true,
      "transition": true,
      "requires": [
        {
          "!": "fill-extrusion-pattern"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        },
        "data-driven styling": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-extrusion-translate": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0,
        0
      ],
      "transition": true,
      "units": "pixels",
      "doc": "The geometry's offset. Values are [x, y] where negatives indicate left and up (on the flat plane), respectively.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "fill-extrusion-translate-anchor": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The fill extrusion is translated relative to the map."
        },
        "viewport": {
          "doc": "The fill extrusion is translated relative to the viewport."
        }
      },
      "doc": "Controls the frame of reference for `fill-extrusion-translate`.",
      "default": "map",
      "requires": [
        "fill-extrusion-translate"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "fill-extrusion-pattern": {
      "type": "resolvedImage",
      "transition": false,
      "doc": "Name of image in sprite to use for drawing images on extruded fills. For seamless patterns, image width and height must be a factor of two (2, 4, 8, ..., 512). Note that zoom-dependent expressions will be evaluated only at integer zoom levels.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        },
        "data-driven styling": {
          "js": "0.49.0",
          "android": "6.5.0",
          "ios": "4.4.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-extrusion-pattern-cross-fade": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.0,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "requires": [
        "line-pattern"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.12.0",
          "android": "11.14.0",
          "ios": "11.14.0"
        }
      },
      "doc": "Controls the transition progress between the image variants of fill-extrusion-pattern. Zero means the first variant is used, one is the second, and in between they are blended together. Both images should be the same size and have the same type (either raster or vector)."
    },
    "fill-extrusion-height": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "units": "meters",
      "doc": "The height with which to extrude this layer.",
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        },
        "data-driven styling": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-extrusion-base": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "units": "meters",
      "doc": "The height with which to extrude the base of this layer. Must be less than or equal to `fill-extrusion-height`.",
      "transition": true,
      "requires": [
        "fill-extrusion-height"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        },
        "data-driven styling": {
          "js": "0.27.0",
          "android": "5.1.0",
          "ios": "3.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-extrusion-height-alignment": {
      "type": "enum",
      "experimental": true,
      "values": {
        "terrain": {
          "doc": "The fill extrusion height follows terrain slope."
        },
        "flat": {
          "doc": "The fill extrusion height is flat over terrain."
        }
      },
      "doc": "Controls the behavior of fill extrusion height over terrain",
      "default": "flat",
      "requires": [
        "fill-extrusion-height"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.8.0",
          "android": "11.8.0",
          "ios": "11.8.0"
        }
      },
      "property-type": "data-constant"
    },
    "fill-extrusion-base-alignment": {
      "type": "enum",
      "experimental": true,
      "values": {
        "terrain": {
          "doc": "The fill extrusion base follows terrain slope."
        },
        "flat": {
          "doc": "The fill extrusion base is flat over terrain."
        }
      },
      "doc": "Controls the behavior of fill extrusion base over terrain",
      "default": "terrain",
      "requires": [
        "fill-extrusion-base"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.8.0",
          "android": "11.8.0",
          "ios": "11.8.0"
        }
      },
      "property-type": "data-constant"
    },
    "fill-extrusion-vertical-gradient": {
      "type": "boolean",
      "default": true,
      "doc": "Whether to apply a vertical gradient to the sides of a fill-extrusion layer. If true, sides will be shaded slightly darker farther down.",
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "0.50.0",
          "android": "7.0.0",
          "ios": "4.7.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "fill-extrusion-ambient-occlusion-intensity": {
      "property-type": "data-constant",
      "type": "number",
      "default": 0.0,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "Controls the intensity of shading near ground and concave angles between walls. Default value 0.0 disables ambient occlusion and values around 0.3 provide the most plausible results for buildings.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "10.7.0",
          "ios": "10.7.0"
        }
      }
    },
    "fill-extrusion-ambient-occlusion-radius": {
      "property-type": "data-constant",
      "type": "number",
      "default": 3.0,
      "minimum": 0,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "Shades area near ground and concave angles between walls where the radius defines only vertical impact. Default value 3.0 corresponds to height of one floor and brings the most plausible results for buildings. This property works only with legacy light. When 3D lights are enabled `fill-extrusion-ambient-occlusion-wall-radius` and `fill-extrusion-ambient-occlusion-ground-radius` are used instead.",
      "requires": [
        "fill-extrusion-edge-radius"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "10.7.0",
          "ios": "10.7.0"
        }
      }
    },
    "fill-extrusion-ambient-occlusion-wall-radius": {
      "property-type": "data-constant",
      "type": "number",
      "experimental": true,
      "default": 3.0,
      "minimum": 0,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "Shades area near ground and concave angles between walls where the radius defines only vertical impact. Default value 3.0 corresponds to height of one floor and brings the most plausible results for buildings.",
      "requires": [
        "lights",
        "fill-extrusion-edge-radius"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-ambient-occlusion-ground-radius": {
      "property-type": "data-constant",
      "type": "number",
      "experimental": true,
      "default": 3.0,
      "minimum": 0,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "The extent of the ambient occlusion effect on the ground beneath the extruded buildings in meters.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-ambient-occlusion-ground-attenuation": {
      "property-type": "data-constant",
      "type": "number",
      "experimental": true,
      "default": 0.69,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "Provides a control to futher fine-tune the look of the ambient occlusion on the ground beneath the extruded buildings. Lower values give the effect a more solid look while higher values make it smoother.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-flood-light-color": {
      "property-type": "data-constant",
      "type": "color",
      "experimental": true,
      "default": "#ffffff",
      "doc": "The color of the flood light effect on the walls of the extruded buildings.",
      "requires": [
        "lights"
      ],
      "use-theme": true,
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-flood-light-intensity": {
      "property-type": "data-constant",
      "type": "number",
      "experimental": true,
      "default": 0.0,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "The intensity of the flood light color.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-flood-light-wall-radius": {
      "property-type": "data-driven",
      "type": "number",
      "experimental": true,
      "units": "meters",
      "default": 0,
      "minimum": 0,
      "doc": "The extent of the flood light effect on the walls of the extruded buildings in meters.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "feature",
          "feature-state"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-flood-light-ground-radius": {
      "property-type": "data-driven",
      "type": "number",
      "experimental": true,
      "units": "meters",
      "default": 0,
      "doc": "The extent of the flood light effect on the ground beneath the extruded buildings in meters. Note: this experimental property is evaluated once per tile, during tile initialization. Changing the property value could trigger tile reload. The `feature-state` styling is deprecated and will get removed soon.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "feature",
          "feature-state"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-flood-light-ground-attenuation": {
      "property-type": "data-constant",
      "type": "number",
      "experimental": true,
      "default": 0.69,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "Provides a control to futher fine-tune the look of the flood light on the ground beneath the extruded buildings. Lower values give the effect a more solid look while higher values make it smoother.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-vertical-scale": {
      "property-type": "data-constant",
      "type": "number",
      "experimental": true,
      "default": 1.0,
      "minimum": 0.0,
      "doc": "A global multiplier that can be used to scale base, height, AO, and flood light of the fill extrusions.",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "fill-extrusion-rounded-roof": {
      "property-type": "data-constant",
      "type": "boolean",
      "default": true,
      "experimental": true,
      "doc": "Indicates whether top edges should be rounded when fill-extrusion-edge-radius has a value greater than 0. If false, rounded edges are only applied to the sides. Default is true.",
      "requires": [
        "fill-extrusion-edge-radius"
      ],
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "10.10.0",
          "ios": "10.10.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      }
    },
    "fill-extrusion-cutoff-fade-range": {
      "type": "number",
      "default": 0.0,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "This parameter defines the range for the fade-out effect before an automatic content cutoff on pitched map views. Fade out is implemented by scaling down and removing buildings in the fade range in a staggered fashion. Opacity is not changed. The fade range is expressed in relation to the height of the map view. A value of 1.0 indicates that the content is faded to the same extent as the map's height in pixels, while a value close to zero represents a sharp cutoff. When the value is set to 0.0, the cutoff is completely disabled. Note: The property has no effect on the map if terrain is enabled.",
      "transition": false,
      "expression": {
        "interpolated": false
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "property-type": "data-constant"
    },
    "fill-extrusion-front-cutoff": {
      "type": "array",
      "private": true,
      "experimental": true,
      "value": "number",
      "property-type": "data-constant",
      "transition": false,
      "expression": {
        "interpolated": true,
        "parameters": ["zoom"]
      },
      "length": 3,
      "default": [0.0, 0.0, 1.0],
      "minimum": [0.0, 0.0, 0.0],
      "maximum": [1.0, 1.0, 1.0],
      "doc": "An array for configuring the fade-out effect for the front cutoff of content on pitched map views. It contains three values: start, range and final opacity. The start parameter defines the point at which the fade-out effect begins, with smaller values causing the effect to start earlier. The range parameter specifies how long the fade-out effect will last. A value of 0.0 for range makes content disappear immediately without a fade-out effect. The final opacity determines content opacity at the end of the fade-out effect. A value of 1.0 for final opacity means that the cutoff is completely disabled. Note: The property has no effect on the map if terrain or globe projection is enabled.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.21.0",
          "android": "11.21.0",
          "ios": "11.21.0"
        }
      }
    },
    "fill-extrusion-emissive-strength": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.8.0",
          "android": "11.8.0",
          "ios": "11.8.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light",
          "feature-state"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-extrusion-line-width": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "experimental": true,
      "units": "meters",
      "doc": "If a non-zero value is provided, it sets the fill-extrusion layer into wall rendering mode. The value is used to render the feature with the given width over the outlines of the geometry. Note: This property is experimental and some other fill-extrusion properties might not be supported with non-zero line width.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.7.0",
          "android": "11.7.0",
          "ios": "11.7.0"

        },
        "data-driven styling": {
          "js": "3.7.0",
          "android": "11.7.0",
          "ios": "11.7.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "fill-extrusion-cast-shadows": {
      "type": "boolean",
      "default": true,
      "doc": "Enable/Disable shadow casting for this layer",
      "transition": false,
      "sdk-support": {
          "basic functionality": {
          "js": "3.7.0",
          "android": "11.8.0",
          "ios": "11.8.0"
        }
      },
      "property-type": "data-constant"
    }
  },
  "paint_building": {
    "building-opacity": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "doc": "The opacity of the entire procedural buildings layer. This is rendered on a per-layer, not per-feature, basis, and data-driven styling is not available.",
      "transition": true,
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "building-ambient-occlusion-intensity": {
      "property-type": "data-constant",
      "type": "number",
      "default": 0,
      "minimum": 0,
      "maximum": 1,
      "private": true,
      "expression": {
        "interpolated": false,
        "parameters": [
        ]
      },
      "transition": true,
      "doc": "Controls the intensity of ambient occlusion when shading concave angles between walls and roof crevices and windows.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-ambient-occlusion-ground-intensity": {
      "property-type": "data-constant",
      "type": "number",
      "default": 0,
      "minimum": 0,
      "maximum": 1,
      "private": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "Controls the intensity of shading near ground",
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-ambient-occlusion-ground-radius": {
      "property-type": "data-constant",
      "type": "number",
      "private": true,
      "default": 3.0,
      "minimum": 0,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "doc": "The extent of the ambient occlusion effect on the ground beneath the procedural buildings in meters.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-ambient-occlusion-ground-attenuation": {
      "property-type": "data-constant",
      "type": "number",
      "private": true,
      "default": 0.69,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "Provides a control to further fine-tune the look of the ambient occlusion on the ground beneath the procedural buildings. Lower values give the effect a more solid look while higher values make it smoother.",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-vertical-scale": {
      "property-type": "data-constant",
      "type": "number",
      "private": true,
      "default": 1.0,
      "minimum": 0.0,
      "doc": "A global multiplier that can be used to scale base and height of the procedural buildings.",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-cast-shadows": {
      "type": "boolean",
      "default": true,
      "doc": "Enable/Disable shadow casting for this layer",
      "transition": false,
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "property-type": "data-constant"
    },
    "building-color": {
      "type": "color",
      "default": "rgba(193, 154, 127, 1)",
      "doc": "Color buildings. This can be styled using different building parts (e.g. window, wall, roof).",
      "private": true,
      "use-theme": true,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "building-emissive-strength": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "maximum": 5,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features. There is no emission for value 0. For value 1.0, only emissive component (no shading) is displayed and values above 1.0 produce light contribution to surrounding area, for some of the parts (e.g. doors).",
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        },
        "data-driven styling": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "building-facade-emissive-chance": {
      "type": "number",
      "default": 0.35,
      "minimum": 0,
      "maximum": 1,
      "doc": "Given as a fraction specifies the likelihood for the facades to be emissive when building-facade is enabled. A value of 0.0 means the window will never be emissive, while a value of 1.0 means the window will always be emissive. This can be used to create variations on a building where some windows are lit and some are not.",
      "private": true,
      "property-type": "data-constant",
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "measure-light",
          "zoom"
        ]
      }
    },
    "building-cutoff-fade-range": {
      "type": "number",
      "private": true,
      "default": 0.0,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "This parameter defines the range for the fade-out effect before an automatic content cutoff on pitched map views. Fade out is implemented by scaling down and removing buildings in the fade range in a staggered fashion. Opacity is not changed. The fade range is expressed in relation to the height of the map view. A value of 1.0 indicates that the content is faded to the same extent as the map's height in pixels, while a value close to zero represents a sharp cutoff. When the value is set to 0.0, the cutoff is completely disabled. Note: The property has no effect on the map if terrain is enabled.",
      "transition": false,
      "expression": {
        "interpolated": false
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      },
      "property-type": "data-constant"
    },
    "building-front-cutoff": {
      "type": "array",
      "private": true,
      "experimental": true,
      "value": "number",
      "property-type": "data-constant",
      "transition": false,
      "expression": {
        "interpolated": true,
        "parameters": ["zoom"]
      },
      "length": 3,
      "default": [0.0, 0.0, 1.0],
      "minimum": [0.0, 0.0, 0.0],
      "maximum": [1.0, 1.0, 1.0],
      "doc": "An array for configuring the fade-out effect for the front cutoff of content on pitched map views. It contains three values: start, range and final opacity. The start parameter defines the point at which the fade-out effect begins, with smaller values causing the effect to start earlier. The range parameter specifies how long the fade-out effect will last. A value of 0.0 for range makes content disappear immediately without a fade-out effect. The final opacity determines content opacity at the end of the fade-out effect. A value of 1.0 for final opacity means that the cutoff is completely disabled. Note: The property has no effect on the map if terrain or globe projection is enabled.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.21.0",
          "android": "11.21.0",
          "ios": "11.21.0"
        }
      }
    },
    "building-flood-light-color": {
      "property-type": "data-constant",
      "type": "color",
      "private": true,
      "default": "#ffffff",
      "doc": "The color of the flood light effect on the walls of the buildings.",
      "requires": [
        "lights"
      ],
      "use-theme": true,
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-flood-light-intensity": {
      "property-type": "data-constant",
      "type": "number",
      "private": true,
      "default": 0.0,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "The intensity of the flood light color.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    },
    "building-flood-light-ground-attenuation": {
      "property-type": "data-constant",
      "type": "number",
      "private": true,
      "default": 0.69,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "Provides a control to futher fine-tune the look of the flood light on the ground beneath the buildings. Lower values give the effect a more solid look while higher values make it smoother.",
      "requires": [
        "lights"
      ],
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.16.0",
          "android": "11.16.0",
          "ios": "11.16.0"
        }
      }
    }
  },
  "paint_line": {
    "line-opacity": {
      "type": "number",
      "doc": "The opacity at which the line will be drawn.",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "line-color": {
      "type": "color",
      "doc": "The color with which the line will be drawn.",
      "default": "#000000",
      "use-theme": true,
      "transition": true,
      "requires": [
        {
          "!": "line-pattern"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.23.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "line-translate": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0,
        0
      ],
      "transition": true,
      "units": "pixels",
      "doc": "The geometry's offset. Values are [x, y] where negatives indicate left and up, respectively.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "line-translate-anchor": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The line is translated relative to the map."
        },
        "viewport": {
          "doc": "The line is translated relative to the viewport."
        }
      },
      "doc": "Controls the frame of reference for `line-translate`.",
      "default": "map",
      "requires": [
        "line-translate"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "line-width": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "transition": true,
      "units": "pixels",
      "doc": "Stroke thickness.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.39.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light",
          "line-progress"
        ]
      },
      "property-type": "data-driven"
    },
    "line-gap-width": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "doc": "Draws a line casing outside of a line's actual path. Value indicates the width of the inner gap.",
      "transition": true,
      "units": "pixels",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "line-offset": {
      "type": "number",
      "default": 0,
      "doc": "The line's offset. For linear features, a positive value offsets the line to the right, relative to the direction of the line, and a negative value to the left. For polygon features, a positive value results in an inset, and a negative value results in an outset.",
      "transition": true,
      "units": "pixels",
      "sdk-support": {
        "basic functionality": {
          "js": "0.12.1",
          "android": "3.0.0",
          "ios": "3.1.0"
        },
        "data-driven styling": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "line-blur": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "pixels",
      "doc": "Blur applied to the line, in pixels.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "line-dasharray": {
      "type": "array",
      "value": "number",
      "doc": "Specifies the lengths of the alternating dashes and gaps that form the dash pattern. The lengths are later scaled by the line width. To convert a dash length to pixels, multiply the length by the current line width. Note that GeoJSON sources with `lineMetrics: true` specified won't render dashed lines to the expected scale. Also note that zoom-dependent expressions will be evaluated only at integer zoom levels.",
      "minimum": 0,
      "transition": false,
      "units": "line widths",
      "requires": [
        {
          "!": "line-pattern"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "2.3.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "line-pattern": {
      "type": "resolvedImage",
      "transition": false,
      "doc": "Name of image in sprite to use for drawing image lines. For seamless patterns, image width must be a factor of two (2, 4, 8, ..., 512). Note that zoom-dependent expressions will be evaluated only at integer zoom levels.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.49.0",
          "android": "6.5.0",
          "ios": "4.4.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "line-pattern-cross-fade": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.0,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "requires": [
        "line-pattern"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.12.0",
          "android": "11.14.0",
          "ios": "11.14.0"
        }
      },
      "doc": "Controls the transition progress between the image variants of line-pattern. Zero means the first variant is used, one is the second, and in between they are blended together. Both images should be the same size and have the same type (either raster or vector)."
    },
    "line-gradient": {
      "type": "color",
      "doc": "A gradient used to color a line feature at various distances along its length. Defined using a `step` or `interpolate` expression which outputs a color for each corresponding `line-progress` input value. `line-progress` is a percentage of the line feature's total length as measured on the webmercator projected coordinate plane (a `number` between `0` and `1`). Can only be used with GeoJSON sources that specify `\"lineMetrics\": true`.",
      "example": [
        "interpolate",
        [ "linear" ],
        [ "line-progress" ],
        0,
        "blue",
        0.1,
        "royalblue",
        0.3,
        "cyan",
        0.5,
        "lime",
        0.7,
        "yellow",
        1,
        "red"
      ],
      "use-theme": true,
      "transition": false,
      "requires": [
        {
          "!": "line-pattern"
        },
        {
          "source": "geojson",
          "has": {
            "lineMetrics": true
          }
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.45.0",
          "android": "6.5.0",
          "ios": "4.4.0"
        },
        "data-driven styling": {}
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "line-progress"
        ]
      },
      "property-type": "color-ramp"
    },
    "line-trim-offset": {
      "type": "array",
      "value": "number",
      "doc": "The line part between [trim-start, trim-end] will be painted using `line-trim-color,` which is transparent by default to produce a route vanishing effect. The line trim-off offset is based on the whole line range [0.0, 1.0].",
      "length": 2,
      "default": [0.0, 0.0],
      "minimum": [0.0, 0.0],
      "maximum": [1.0, 1.0],
      "transition": false,
      "requires": [
        {
            "source": "geojson",
            "has": {
                "lineMetrics": true
            }
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "2.9.0",
          "android": "10.5.0",
          "ios": "10.5.0"
        }
      },
      "property-type": "constant"
    },
    "line-trim-fade-range": {
      "type": "array",
      "value": "number",
      "doc": "The fade range for the trim-start and trim-end points is defined by the `line-trim-offset` property. The first element of the array represents the fade range from the trim-start point toward the end of the line, while the second element defines the fade range from the trim-end point toward the beginning of the line. The fade result is achieved by interpolating between `line-trim-color` and the color specified by the `line-color` or the `line-gradient` property.",
      "experimental": true,
      "length": 2,
      "default": [0.0, 0.0],
      "minimum": [0.0, 0.0],
      "maximum": [1.0, 1.0],
      "transition": false,
      "requires": [
        "line-trim-offset",
        {
          "source": "geojson",
          "has": {
            "lineMetrics": true
          }
        }
      ],
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.6.0",
          "android": "11.6.0",
          "ios": "11.6.0"
        }
      },
      "property-type": "data-constant"
    },
    "line-trim-color": {
      "type": "color",
      "doc": "The color to be used for rendering the trimmed line section that is defined by the `line-trim-offset` property.",
      "experimental": true,
      "default": "transparent",
      "use-theme": true,
      "transition": true,
      "requires": [
        "line-trim-offset",
        {
          "source": "geojson",
          "has": {
              "lineMetrics": true
          }
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.6.0",
          "android": "11.6.0",
          "ios": "11.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    },
    "line-emissive-strength": {
      "type": "number",
      "default": 0.0,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.17.0",
          "android": "11.17.0",
          "ios": "11.17.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light",
          "line-progress"
        ]
      },
      "property-type": "data-driven"
    },
    "line-border-width": {
      "type": "number",
      "doc": "The width of the line border. A value of zero means no border.",
      "default": 0.0,
      "minimum": 0.0,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "10.9.0",
          "ios": "10.9.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "10.9.0",
          "ios": "10.9.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state"
        ]
      },
      "property-type": "data-driven"
    },
    "line-border-color": {
      "type": "color",
      "doc": "The color of the line border. If line-border-width is greater than zero and the alpha value of this color is 0 (default), the color for the border will be selected automatically based on the line color.",
      "default": "rgba(0, 0, 0, 0)",
      "use-theme": true,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "10.9.0",
          "ios": "10.9.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "10.9.0",
          "ios": "10.9.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state"
        ]
      },
      "property-type": "data-driven"
    },
    "line-border-gradient": {
      "type": "color",
      "experimental": true,
      "doc": "A gradient used to color the border of a line feature at various distances along its length. Defined using a `step` or `interpolate` expression which outputs a color for each corresponding `line-progress` input value. `line-progress` is a percentage of the line feature's total length as measured on the webmercator projected coordinate plane (a `number` between `0` and `1`). Takes precedence over `line-border-color`. Has no effect unless `line-border-width` is greater than zero. Can only be used with GeoJSON sources that specify `\"lineMetrics\": true`.",
      "example": [
        "step",
        [
          "line-progress"
        ],
        "blue",
        0.5,
        "gray"
      ],
      "use-theme": true,
      "transition": false,
      "requires": [
        {
          "!": "line-pattern"
        },
        {
          "source": "geojson",
          "has": {
            "lineMetrics": true
          }
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.28.0",
          "android": "11.28.0",
          "ios": "11.28.0"
        },
        "data-driven styling": {}
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "line-progress"
        ]
      },
      "property-type": "color-ramp"
    },
    "line-occlusion-opacity": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "maximum": 1,
      "doc": "Opacity multiplier (multiplies line-opacity value) of the line part that is occluded by 3D objects. Value 0 hides occluded part, value 1 means the same opacity as non-occluded part. The property is not supported when `line-opacity` has data-driven styling.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "transition": true,
      "property-type": "data-constant"
    },
    "line-blend-mode": {
      "type": "enum",
      "values": {
        "default": { "doc": "Normal non-blended rendering mode." },
        "multiply": { "doc": "Line features are composited using multiply blending." },
        "additive": { "doc": "Line features are composited using additive density accumulation with tone mapping." }
      },
      "doc": "Controls the blend mode used for rendering line features.",
      "default": "default",
      "experimental": true,
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.21.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "line-blend-additive-clamp": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "doc": "Controls the brightness of lines in additive blend mode. When set to 0 (the default), brightness is adjusted automatically based on the density of lines visible on screen. Increase this value if the lines appear too bright, or decrease it if they appear too faint.",
      "experimental": true,
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.21.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "line-cutout-depth": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "units": "meters",
      "doc": "Depth offset along the camera, in meters. When set to a non-zero value, applied cutout opacity is 0.",
      "experimental": true,
      "private": true,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "android": "11.29.0",
          "ios": "11.29.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    }
  },
  "paint_circle": {
    "circle-radius": {
      "type": "number",
      "default": 5,
      "minimum": 0,
      "transition": true,
      "units": "pixels",
      "doc": "Circle radius.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.18.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "circle-color": {
      "type": "color",
      "default": "#000000",
      "doc": "The fill color of the circle.",
      "use-theme": true,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.18.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "circle-blur": {
      "type": "number",
      "default": 0,
      "doc": "Amount to blur the circle. 1 blurs the circle such that only the centerpoint is full opacity. Setting a negative value renders the blur as an inner glow effect.",
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.20.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "circle-opacity": {
      "type": "number",
      "doc": "The opacity at which the circle will be drawn.",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.20.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "circle-translate": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0,
        0
      ],
      "transition": true,
      "units": "pixels",
      "doc": "The geometry's offset. Values are [x, y] where negatives indicate left and up, respectively.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "circle-translate-anchor": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The circle is translated relative to the map."
        },
        "viewport": {
          "doc": "The circle is translated relative to the viewport."
        }
      },
      "doc": "Controls the frame of reference for `circle-translate`.",
      "default": "map",
      "requires": [
        "circle-translate"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "circle-pitch-scale": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "Circles are scaled according to their apparent distance to the camera."
        },
        "viewport": {
          "doc": "Circles are not scaled."
        }
      },
      "default": "map",
      "doc": "Controls the scaling behavior of the circle when the map is pitched.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.21.0",
          "android": "4.2.0",
          "ios": "3.4.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "circle-pitch-alignment": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The circle is aligned to the plane of the map."
        },
        "viewport": {
          "doc": "The circle is aligned to the plane of the viewport."
        }
      },
      "default": "viewport",
      "doc": "Orientation of circle when map is pitched.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.39.0",
          "android": "5.2.0",
          "ios": "3.7.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "circle-stroke-width": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "pixels",
      "doc": "The width of the circle's stroke. Strokes are placed outside of the `circle-radius`.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        },
        "data-driven styling": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "circle-stroke-color": {
      "type": "color",
      "default": "#000000",
      "doc": "The stroke color of the circle.",
      "use-theme": true,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        },
        "data-driven styling": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "circle-stroke-opacity": {
      "type": "number",
      "doc": "The opacity of the circle's stroke.",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        },
        "data-driven styling": {
          "js": "0.29.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "circle-emissive-strength": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
   }
  },
  "paint_heatmap": {
    "heatmap-radius": {
      "type": "number",
      "default": 30,
      "minimum": 1,
      "transition": true,
      "units": "pixels",
      "doc": "Radius of influence of one heatmap point in pixels. Increasing the value makes the heatmap smoother, but less detailed. `queryRenderedFeatures` on heatmap layers will return points within this radius.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.41.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        },
        "data-driven styling": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "heatmap-weight": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "transition": false,
      "doc": "A measure of how much an individual point contributes to the heatmap. A value of 10 would be equivalent to having 10 points of weight 1 in the same spot. Especially useful when combined with clustering.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.41.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        },
        "data-driven styling": {
          "js": "0.41.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "property-type": "data-driven"
    },
    "heatmap-intensity": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "transition": true,
      "doc": "Similar to `heatmap-weight` but controls the intensity of the heatmap globally. Primarily used for adjusting the heatmap based on zoom level.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.41.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "heatmap-color": {
      "type": "color",
      "default": [
        "interpolate",
        [
          "linear"
        ],
        [
          "heatmap-density"
        ],
        0,
        "rgba(0, 0, 255, 0)",
        0.1,
        "royalblue",
        0.3,
        "cyan",
        0.5,
        "lime",
        0.7,
        "yellow",
        1,
        "red"
      ],
      "doc": "Defines the color of each pixel based on its density value in a heatmap. Should be an expression that uses `[\"heatmap-density\"]` as input.",
      "use-theme": true,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "0.41.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        },
        "data-driven styling": {}
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "heatmap-density"
        ]
      },
      "property-type": "color-ramp"
    },
    "heatmap-opacity": {
      "type": "number",
      "doc": "The global opacity at which the heatmap layer will be drawn.",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.41.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    }
  },
  "paint_symbol": {
    "icon-opacity": {
      "doc": "The opacity at which the icon will be drawn.",
      "type": "number",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "icon-occlusion-opacity": {
      "doc": "The opacity at which the icon will be drawn in case of being depth occluded. Absent value means full occlusion against terrain only.",
      "type": "number",
      "minimum": 0,
      "maximum": 1,
      "default": 0,
      "transition": true,
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.6.0"
        },
        "data-driven styling": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "icon-emissive-strength": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light",
          "feature-state"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "text-emissive-strength": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light",
          "feature-state"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "icon-color": {
      "type": "color",
      "default": "#000000",
      "use-theme": true,
      "transition": true,
      "doc": "The color of the icon. This can only be used with [SDF icons](/help/troubleshooting/using-recolorable-images-in-mapbox-maps/).",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "icon-halo-color": {
      "type": "color",
      "default": "rgba(0, 0, 0, 0)",
      "use-theme": true,
      "transition": true,
      "doc": "The color of the icon's halo. Icon halos can only be used with [SDF icons](/help/troubleshooting/using-recolorable-images-in-mapbox-maps/).",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "icon-halo-width": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "pixels",
      "doc": "Distance of halo to the icon outline.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "icon-halo-blur": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "pixels",
      "doc": "Fade out the halo towards the outside.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "icon-translate": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0,
        0
      ],
      "transition": true,
      "appearance": true,
      "units": "pixels",
      "doc": "Distance that the icon's anchor is moved from its original placement. Positive values indicate right and down, while negative values indicate left and up.",
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-translate-anchor": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "Icons are translated relative to the map."
        },
        "viewport": {
          "doc": "Icons are translated relative to the viewport."
        }
      },
      "doc": "Controls the frame of reference for `icon-translate`.",
      "default": "map",
      "requires": [
        "icon-image",
        "icon-translate"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-image-cross-fade": {
      "type": "number",
      "property-type": "data-constant",
      "default": 0.0,
      "minimum": 0,
      "maximum": 1,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "requires": [
        "icon-image"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "doc": "Controls the transition progress between the image variants of icon-image. Zero means the first variant is used, one is the second, and in between they are blended together. . Both images should be the same size and have the same type (either raster or vector)."
    },
    "text-opacity": {
      "type": "number",
      "doc": "The opacity at which the text will be drawn.",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "text-occlusion-opacity": {
      "type": "number",
      "doc": "The opacity at which the text will be drawn in case of being depth occluded. Absent value means full occlusion against terrain only.",
      "minimum": 0,
      "maximum": 1,
      "default": 0,
      "transition": true,
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.6.0"
        },
        "data-driven styling": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.6.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "text-color": {
      "type": "color",
      "doc": "The color with which the text will be drawn.",
      "default": "#000000",
      "use-theme": true,
      "transition": true,
      "overridable": true,
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "text-halo-color": {
      "type": "color",
      "default": "rgba(0, 0, 0, 0)",
      "use-theme": true,
      "transition": true,
      "doc": "The color of the text's halo, which helps it stand out from backgrounds.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "text-halo-width": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "pixels",
      "doc": "Distance of halo to the font outline. Max text halo width is 1/4 of the font-size.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "text-halo-blur": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "pixels",
      "doc": "The halo's fadeout distance towards the outside.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        },
        "data-driven styling": {
          "js": "0.33.0",
          "android": "5.0.0",
          "ios": "3.5.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature",
          "feature-state",
          "measure-light"
        ]
      },
      "appearance": true,
      "property-type": "data-driven"
    },
    "text-translate": {
      "type": "array",
      "value": "number",
      "length": 2,
      "default": [
        0,
        0
      ],
      "transition": true,
      "appearance": true,
      "units": "pixels",
      "doc": "Distance that the text's anchor is moved from its original placement. Positive values indicate right and down, while negative values indicate left and up.",
      "requires": [
        "text-field"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "text-translate-anchor": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The text is translated relative to the map."
        },
        "viewport": {
          "doc": "The text is translated relative to the viewport."
        }
      },
      "doc": "Controls the frame of reference for `text-translate`.",
      "default": "map",
      "requires": [
        "text-field",
        "text-translate"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "icon-color-saturation": {
      "type": "number",
      "default": 0,
      "minimum": -1,
      "maximum": 1,
      "transition": false,
      "doc": "Increase or reduce the saturation of the symbol icon.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.1.0",
          "android": "11.1.0",
          "ios": "11.1.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "icon-color-contrast": {
      "type": "number",
      "default": 0,
      "minimum": -1,
      "maximum": 1,
      "transition": false,
      "doc": "Increase or reduce the contrast of the symbol icon.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.5.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "icon-color-brightness-min": {
      "type": "number",
      "doc": "Increase or reduce the brightness of the symbols. The value is the minimum brightness.",
      "default": 0,
      "minimum": 0,
      "maximum": 1,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.5.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "icon-color-brightness-max": {
      "type": "number",
      "doc": "Increase or reduce the brightness of the symbols. The value is the maximum brightness.",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0",
          "android": "11.5.0",
          "ios": "11.5.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    },
    "symbol-z-offset": {
      "type": "number",
      "doc": "Specifies an uniform elevation from the ground, in meters.",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "appearance": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.7.0",
          "android": "11.7.0",
          "ios": "11.7.0"
        },
        "data-driven styling": {
          "js": "3.7.0",
          "android": "11.7.0",
          "ios": "11.7.0"
        }
      },
      "requires": [
        {
          "symbol-z-elevate": true
        }
      ],
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "feature"
        ]
      },
      "property-type": "data-driven"
    },
    "placement-priority": {
      "type": "number",
      "doc": "In-group placement priority. Higher values are placed first within the symbol's placement group.",
      "default": 0,
      "minimum": 0,
      "transition": false,
      "appearance": true,
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "pitch",
          "feature",
          "feature-state",
          "measure-light",
          "distance-from-center"
        ]
      },
      "property-type": "data-driven",
      "sdk-support": {
        "basic functionality": {},
        "data-driven styling": {}
      },
      "private": true,
      "experimental": true
    },
    "placement-group": {
      "type": "string",
      "doc": "Assigns this symbol layer to the specified placement-group layer, placing its symbols as if they were drawn within that group. When unset, the layer's symbols use an implicitly created placement group located at the same position as the symbol layer.",
      "transition": false,
      "appearance": true,
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom",
          "pitch",
          "feature",
          "feature-state",
          "measure-light",
          "distance-from-center"
        ]
      },
      "property-type": "data-driven",
      "sdk-support": {
        "basic functionality": {},
        "data-driven styling": {}
      },
      "private": true,
      "experimental": true
    }
  },
  "paint_raster": {
    "raster-opacity": {
      "type": "number",
      "doc": "The opacity at which the image will be drawn.",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-color": {
      "type": "color",
      "doc": "Defines the color ramp used to colorize a raster layer, parameterized by the `[\"raster-value\"]` expression and evaluated over the range specified by `raster-color-range`. Raster values are spaced evenly across the range by default.",
      "use-theme": true,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {}
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "raster-value"
        ]
      },
      "property-type": "color-ramp"
    },
    "raster-color-mix": {
      "type": "array",
      "default": [
        0.2126,
        0.7152,
        0.0722,
        0.0
      ],
      "length": 4,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "requires": [
        "raster-color"
      ],
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "doc": "When `raster-color` is active, specifies how the raster value is computed from a non-`rasterarray` source's channels, using the equation `mix.r * src.r + mix.g * src.g + mix.b * src.b + mix.a`. The first three components weight the source's red, green, and blue channels; the fourth is a constant offset and is not multiplied by source alpha. Source alpha is carried through and applied as opacity to the colorized result. The default corresponds to RGB luminosity. `rasterarray` sources ignore this property, as their raster value is decoded directly from the source data.",
      "example": [
        0.2126,
        0.7152,
        0.0722,
        0.0
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "raster-color-range": {
      "type": "array",
      "length": 2,
      "value": "number",
      "property-type": "data-constant",
      "transition": true,
      "requires": [
        "raster-color"
      ],
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "doc": "When `raster-color` is active, specifies the range of raster values mapped onto the color ramp, from the start of the ramp (low bound) to its end (high bound). For `rasterarray` sources the raster value is the decoded source data in the source's own units, and the source's stated data range is used when this property is unspecified. For other raster sources the raster value is computed from the source's channels via `raster-color-mix`. Defaults to `[0, 1]` when no range is otherwise available.",
      "example": [
        0.5,
        10
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      }
    },
    "raster-color-scale": {
      "type": "enum",
      "doc": "When `raster-color` is active, specifies how raster values are distributed across the color ramp over the range specified by `raster-color-range`.",
      "values": {
        "linear": {
          "doc": "Raster values are spaced evenly across the color ramp."
        },
        "log": {
          "doc": "Raster values are spaced logarithmically, giving more of the color ramp to smaller values. Useful for data concentrated near the low end of a wide range."
        }
      },
      "default": "linear",
      "requires": [
        "raster-color",
        {"source": "raster-array"}
      ],
      "expression": {
        "interpolated": false,
        "parameters": []
      },
      "property-type": "data-constant",
      "experimental": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.29.0",
          "android": "11.30.0",
          "ios": "11.30.0"
        }
      }
    },
    "raster-hue-rotate": {
      "type": "number",
      "default": 0,
      "period": 360,
      "transition": true,
      "units": "degrees",
      "doc": "Rotates hues around the color wheel.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-brightness-min": {
      "type": "number",
      "doc": "Increase or reduce the brightness of the image. The value is the minimum brightness.",
      "default": 0,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-brightness-max": {
      "type": "number",
      "doc": "Increase or reduce the brightness of the image. The value is the maximum brightness.",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-saturation": {
      "type": "number",
      "doc": "Increase or reduce the saturation of the image.",
      "default": 0,
      "minimum": -1,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-contrast": {
      "type": "number",
      "doc": "Increase or reduce the contrast of the image.",
      "default": 0,
      "minimum": -1,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-resampling": {
      "type": "enum",
      "doc": "The resampling/interpolation method to use for overscaling, also known as texture magnification filter",
      "values": {
        "linear": {
          "doc": "(Bi)linear filtering interpolates pixel values using the weighted average of the four closest original source pixels creating a smooth but blurry look when overscaled"
        },
        "nearest": {
          "doc": "Nearest neighbor filtering interpolates pixel values using the nearest original source pixel creating a sharp but pixelated look when overscaled"
        }
      },
      "default": "linear",
      "sdk-support": {
        "basic functionality": {
          "js": "0.47.0",
          "android": "6.3.0",
          "ios": "4.2.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-fade-duration": {
      "type": "number",
      "default": 300,
      "minimum": 0,
      "transition": false,
      "units": "milliseconds",
      "doc": "Fade duration when a new tile is added.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-emissive-strength": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.1.0",
          "android": "11.1.0",
          "ios": "11.1.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-array-band": {
      "type": "string",
      "required": false,
      "experimental": true,
      "property-type": "data-constant",
      "transition": false,
      "requires": [
        {
          "source": "raster-array"
        }
      ],
      "doc": "Displayed band of raster array source layer. Defaults to the first band if not set.",
      "example": "band-name",
      "sdk-support": {
        "basic functionality": {
          "js": "3.1.0",
          "android": "11.1.0",
          "ios": "11.1.0"
        }
      }
    },
    "raster-elevation": {
      "type": "number",
      "doc": "Defines an uniform elevation from the base specified in raster-elevation-reference, in meters.",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "experimental": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.1.0",
          "android": "11.2.0",
          "ios": "11.2.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-elevation-reference": {
      "type": "enum",
      "doc": "Selects the base of raster-elevation.",
      "values": {
        "sea": {
          "doc": "Use this mode to elevate raster layers relative to the sea level."
        },
        "ground": {
          "doc": "Use this mode to elevate raster layers relative to the ground's height below them."
        }
      },
      "default": "sea",
      "transition": false,
      "experimental": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.19.0",
          "android": "11.19.0",
          "ios": "11.19.0"
        }
      },
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant"
    }
  },
  "paint_raster-particle": {
    "raster-particle-array-band": {
      "type": "string",
      "required": false,
      "property-type": "data-constant",
      "transition": false,
      "doc": "Displayed band of raster array source layer",
      "example": "\"1713348000\"",
      "sdk-support": {
        "basic functionality": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        }
      }
    },
    "raster-particle-count": {
      "type": "number",
      "doc": "Defines the amount of particles per tile.",
      "default": 512,
      "minimum": 1,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        }
      },
      "property-type": "data-constant"
    },
    "raster-particle-color": {
      "type": "color",
      "doc": "Defines a color map by which to colorize a raster particle layer, parameterized by the `[\"raster-particle-speed\"]` expression and evaluated at 256 uniformly spaced steps over the range specified by `raster-particle-max-speed`.",
      "use-theme": true,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        },
        "data-driven styling": {}
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "raster-particle-speed"
        ]
      },
      "property-type": "color-ramp"
    },
    "raster-particle-max-speed": {
      "type": "number",
      "doc": "Defines the maximum speed for particles. Velocities with magnitudes equal to or exceeding this value are clamped to the max value.",
      "default": 1,
      "minimum": 1,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        }
      },
      "property-type": "data-constant"
    },
    "raster-particle-speed-factor": {
      "type": "number",
      "doc": "Defines a coefficient for the speed of particles’ motion.",
      "default": 0.2,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-particle-fade-opacity-factor": {
      "type": "number",
      "doc": "Defines defines the opacity coefficient applied to the faded particles in each frame. In practice, this property controls the length of the particle tail.",
      "default": 0.98,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "raster-particle-reset-rate-factor": {
      "type": "number",
      "doc": "Defines a coefficient for a time period at which particles will restart at a random position, to avoid degeneration (empty areas without particles).",
      "default": 0.8,
      "minimum": 0,
      "maximum": 1,
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "3.3.0",
          "android": "11.4.0",
          "ios": "11.4.0"
        }
      },
      "property-type": "data-constant"
    },
    "raster-particle-elevation": {
      "type": "number",
      "doc": "Defines an uniform elevation from sea level, in meters.",
      "default": 0,
      "minimum": 0,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.7.0",
          "android": "11.7.0",
          "ios": "11.7.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    }
  },
  "paint_hillshade": {
    "hillshade-illumination-direction": {
      "type": "number",
      "default": 335,
      "minimum": 0,
      "maximum": 359,
      "doc": "The direction of the light source used to generate the hillshading with 0 as the top of the viewport if `hillshade-illumination-anchor` is set to `viewport` and due north if `hillshade-illumination-anchor` is set to `map` and no 3d lights enabled. If `hillshade-illumination-anchor` is set to `map` and 3d lights enabled, the direction from 3d lights is used instead.",
      "transition": false,
      "sdk-support": {
        "basic functionality": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "hillshade-illumination-anchor": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The hillshade illumination is relative to the north direction."
        },
        "viewport": {
          "doc": "The hillshade illumination is relative to the top of the viewport."
        }
      },
      "default": "viewport",
      "doc": "Direction of light source when map is rotated.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "hillshade-exaggeration": {
      "type": "number",
      "doc": "Intensity of the hillshade",
      "default": 0.5,
      "minimum": 0,
      "maximum": 1,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "hillshade-shadow-color": {
      "type": "color",
      "default": "#000000",
      "doc": "The shading color of areas that face away from the light source.",
      "use-theme": true,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    },
    "hillshade-highlight-color": {
      "type": "color",
      "default": "#FFFFFF",
      "doc": "The shading color of areas that faces towards the light source.",
      "use-theme": true,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    },
    "hillshade-accent-color": {
      "type": "color",
      "default": "#000000",
      "doc": "The shading color used to accentuate rugged terrain like sharp cliffs and gorges.",
      "use-theme": true,
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.43.0",
          "android": "6.0.0",
          "ios": "4.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    },
    "hillshade-emissive-strength": {
      "type": "number",
      "default": 0.0,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    }
  },
  "paint_background": {
    "background-pitch-alignment": {
      "type": "enum",
      "values": {
        "map": {
          "doc": "The background is aligned to the plane of the map."
        },
        "viewport": {
          "doc": "The background is aligned to the plane of the viewport, covering the whole screen. Note: This mode disables the automatic reordering of the layer when terrain or globe projection is used."
        }
      },
      "default": "map",
      "doc": "Orientation of background layer.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.8.0",
          "android": "11.8.0",
          "ios": "11.8.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": []
      },
      "experimental": true,
      "property-type": "data-constant"
    },
    "background-color": {
      "type": "color",
      "default": "#000000",
      "doc": "The color with which the background will be drawn.",
      "use-theme": true,
      "transition": true,
      "requires": [
        {
          "!": "background-pattern"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    },
    "background-pattern": {
      "type": "resolvedImage",
      "transition": false,
      "doc": "Name of image in sprite to use for drawing an image background. For seamless patterns, image width and height must be a factor of two (2, 4, 8, ..., 512). Note that zoom-dependent expressions will be evaluated only at integer zoom levels.",
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "background-opacity": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "doc": "The opacity at which the background will be drawn.",
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "0.10.0",
          "android": "2.0.1",
          "ios": "2.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "background-emissive-strength": {
      "type": "number",
      "default": 0.0,
      "minimum": 0,
      "transition": true,
      "units": "intensity",
      "doc": "Controls the intensity of light emitted on the source features.",
      "requires": [
        "lights"
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom",
          "measure-light"
        ]
      },
      "property-type": "data-constant"
    }
  },
  "paint_sky": {
    "sky-type": {
      "type": "enum",
      "values": {
        "gradient": {
          "doc": "Renders the sky with a gradient that can be configured with `sky-gradient-radius` and `sky-gradient`."
        },
        "atmosphere": {
          "doc": "Renders the sky with a simulated atmospheric scattering algorithm, the sun direction can be attached to the light position or explicitly set through `sky-atmosphere-sun`."
        }
      },
      "default": "atmosphere",
      "doc": "The type of the sky",
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "sky-atmosphere-sun": {
      "type": "array",
      "value": "number",
      "length": 2,
      "units": "degrees",
      "minimum": [0, 0],
      "maximum": [360, 180],
      "transition": false,
      "doc": "Position of the sun center [a azimuthal angle, p polar angle]. The azimuthal angle indicates the position of the sun relative to 0° north, where degrees proceed clockwise. The polar angle indicates the height of the sun, where 0° is directly above, at zenith, and 90° at the horizon. When this property is ommitted, the sun center is directly inherited from the light position.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      },
      "requires": [
        {
          "sky-type": "atmosphere"
        }
      ],
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "sky-atmosphere-sun-intensity": {
      "type": "number",
      "requires": [
        {
          "sky-type": "atmosphere"
        }
      ],
      "default": 10,
      "minimum": 0,
      "maximum": 100,
      "transition": false,
      "doc": "Intensity of the sun as a light source in the atmosphere (on a scale from 0 to a 100). Setting higher values will brighten up the sky.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      },
      "property-type": "data-constant"
    },
    "sky-gradient-center": {
      "type": "array",
      "requires": [
        {
          "sky-type": "gradient"
        }
      ],
      "value": "number",
      "default": [
        0,
        0
      ],
      "length": 2,
      "units": "degrees",
      "minimum": [0, 0],
      "maximum": [360, 180],
      "transition": false,
      "doc": "Position of the gradient center [a azimuthal angle, p polar angle]. The azimuthal angle indicates the position of the gradient center relative to 0° north, where degrees proceed clockwise. The polar angle indicates the height of the gradient center, where 0° is directly above, at zenith, and 90° at the horizon.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "sky-gradient-radius": {
      "type": "number",
      "requires": [
        {
          "sky-type": "gradient"
        }
      ],
      "default": 90,
      "minimum": 0,
      "maximum": 180,
      "transition": false,
      "doc": "The angular distance (measured in degrees) from `sky-gradient-center` up to which the gradient extends. A value of 180 causes the gradient to wrap around to the opposite direction from `sky-gradient-center`.",
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      },
      "expression": {
        "interpolated": false,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    },
    "sky-gradient": {
      "type": "color",
      "default": [
        "interpolate",
        [
          "linear"
        ],
        [
          "sky-radial-progress"
        ],
        0.8,
        "#87ceeb",
        1,
        "white"
      ],
      "doc": "Defines a radial color gradient with which to color the sky. The color values can be interpolated with an expression using `sky-radial-progress`. The range [0, 1] for the interpolant covers a radial distance (in degrees) of [0, `sky-gradient-radius`] centered at the position specified by `sky-gradient-center`.",
      "use-theme": true,
      "transition": false,
      "requires": [
        {
          "sky-type": "gradient"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        },
        "data-driven styling": {}
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "sky-radial-progress"
        ]
      },
      "property-type": "color-ramp"
    },
    "sky-atmosphere-halo-color": {
      "type": "color",
      "default": "white",
      "doc": "A color applied to the atmosphere sun halo. The alpha channel describes how strongly the sun halo is represented in an atmosphere sky layer.",
      "use-theme": true,
      "transition": false,
      "requires": [
        {
          "sky-type": "atmosphere"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      },
      "property-type": "data-constant"
    },
    "sky-atmosphere-color": {
      "type": "color",
      "default": "white",
      "doc": "A color used to tweak the main atmospheric scattering coefficients. Using white applies the default coefficients giving the natural blue color to the atmosphere. This color affects how heavily the corresponding wavelength is represented during scattering. The alpha channel describes the density of the atmosphere, with 1 maximum density and 0 no density.",
      "use-theme": true,
      "transition": false,
      "requires": [
        {
          "sky-type": "atmosphere"
        }
      ],
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      },
      "property-type": "data-constant"
    },
    "sky-opacity": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "doc": "The opacity of the entire sky layer.",
      "transition": true,
      "sdk-support": {
        "basic functionality": {
          "js": "2.0.0",
          "ios": "10.0.0",
          "android": "10.0.0"
        }
      },
      "expression": {
        "interpolated": true,
        "parameters": [
          "zoom"
        ]
      },
      "property-type": "data-constant"
    }
  },
  "paint_model" : {
    "model-opacity": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "doc": "The opacity of the model layer. Except for zoom, expressions that are data-driven are not supported if using GeoJSON or vector tile as the model layer source.",
      "transition": true,
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state", "zoom"]
      },
      "sdk-support": {
        "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.9.0",
          "android": "11.9.0",
          "ios": "11.9.0"
        }
      },
      "property-type": "data-driven"
    },
    "model-rotation": {
      "type": "array",
      "value": "number",
      "length": 3,
      "default": [0, 0, 0],
      "period": 360,
      "units": "degrees",
      "property-type": "data-driven",
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state", "zoom"]
      },
      "sdk-support": {
         "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "transition": true,
      "doc": "The rotation of the model in euler angles [lon, lat, z]."
    },
    "model-scale": {
      "type": "array",
      "value": "number",
      "length": 3,
      "default": [1, 1, 1],
      "doc": "The scale of the model. Expressions that are zoom-dependent are not supported if using GeoJSON or vector tile as the model layer source.",
      "property-type": "data-driven",
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state", "zoom"]
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "transition": true
    },
    "model-translation": {
      "type": "array",
      "value": "number",
      "length": 3,
      "default": [0, 0, 0],
      "property-type": "data-driven",
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state", "zoom"]
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "transition": true,
      "doc": "The translation of the model in meters in form of [longitudal, latitudal, altitude] offsets."
    },
    "model-color": {
      "type": "color",
      "default": "#ffffff",
      "doc": "The tint color of the model layer. model-color-mix-intensity (defaults to 0) defines tint(mix) intensity - this means that, this color is not used unless model-color-mix-intensity gets value greater than 0. Expressions that depend on measure-light are not supported when using GeoJSON or vector tile as the model layer source.",
      "property-type": "data-driven",
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state", "measure-light", "zoom"]
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "use-theme": true,
      "transition": true
    },
    "model-color-mix-intensity": {
      "type": "number",
      "property-type": "data-driven",
      "default": 0.0,
      "minimum": 0,
      "maximum": 1,
      "doc": "Intensity of model-color (on a scale from 0 to 1) in color mix with original 3D model's colors. Higher number will present a higher model-color contribution in mix. Expressions that depend on measure-light are not supported when using GeoJSON or vector tile as the model layer source.",
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state", "measure-light"]
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "transition": true
    },
    "model-type": {
      "type": "enum",
      "values": {
        "common-3d": {
          "doc": "Integrated to 3D scene, using depth testing, along with terrain, fill-extrusions and custom layer."
        },
        "location-indicator": {
          "doc": "Displayed over other 3D content, occluded by terrain."
        }
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "default": "common-3d",
      "doc": "Defines rendering behavior of model in respect to other 3D scene objects.",
      "property-type": "data-constant"
    },
    "model-cast-shadows": {
      "type": "boolean",
      "default": true,
      "doc": "Enable/Disable shadow casting for this layer",
      "transition": false,
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "property-type": "data-constant"
    },
    "model-receive-shadows": {
      "type": "boolean",
      "default": true,
      "doc": "Enable/Disable shadow receiving for this layer",
      "transition": false,
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "property-type": "data-constant"
    },
    "model-ambient-occlusion-intensity": {
      "type": "number",
      "default": 1.0,
      "minimum": 0,
      "maximum": 1,
      "doc": "Intensity of the ambient occlusion if present in the 3D model.",
      "expression": {
        "interpolated": true,
        "parameters": ["zoom"]
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "property-type": "data-constant",
      "transition": true
    },
    "model-emissive-strength": {
      "type": "number",
      "property-type": "data-driven",
      "default": 0.0,
      "minimum": 0,
      "maximum": 5,
      "units": "intensity",
      "doc": "Strength of the emission. There is no emission for value 0. For value 1.0, only emissive component (no shading) is displayed and values above 1.0 produce light contribution to surrounding area, for some of the parts (e.g. doors). Expressions that depend on measure-light are only supported as a global layer value (and not for each feature) when using GeoJSON or vector tile as the model layer source.",
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state", "measure-light"]
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "transition": true
    },
    "model-roughness": {
      "type": "number",
      "default": 1,
      "minimum": 0,
      "maximum": 1,
      "doc": "Material roughness. Material is fully smooth for value 0, and fully rough for value 1. Affects only layers using batched-model source.",
      "property-type": "data-driven",
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state"]
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "transition": true
    },
    "model-height-based-emissive-strength-multiplier": {
      "type": "array",
      "default": [
        1,
        1,
        1,
        1,
        0
      ],
      "length": 5,
      "value": "number",
      "doc": "Emissive strength multiplier along model height (gradient begin, gradient end, value at begin, value at end, gradient curve power (logarithmic scale, curve power = pow(10, val)).",
      "property-type": "data-driven",
      "expression": {
        "interpolated": true,
        "parameters": ["feature", "feature-state", "measure-light"]
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        },
        "data-driven styling": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "transition": true
    },
    "model-cutoff-fade-range": {
      "type": "number",
      "default": 0.0,
      "minimum": 0.0,
      "maximum": 1.0,
      "doc": "This parameter defines the range for the fade-out effect before an automatic content cutoff on pitched map views. The automatic cutoff range is calculated according to the minimum required zoom level of the source and layer. The fade range is expressed in relation to the height of the map view. A value of 1.0 indicates that the content is faded to the same extent as the map's height in pixels, while a value close to zero represents a sharp cutoff. When the value is set to 0.0, the cutoff is completely disabled. Note: The property has no effect on the map if terrain is enabled.",
      "transition": false,
      "expression": {
        "interpolated": false
      },
      "sdk-support": {
          "basic functionality": {
          "js": "3.0.0",
          "android": "11.0.0",
          "ios": "11.0.0"
        }
      },
      "property-type": "data-constant"
    },
    "model-front-cutoff": {
      "type": "array",
      "private": true,
      "value": "number",
      "property-type": "data-constant",
      "transition": false,
      "expression": {
        "interpolated": true,
        "parameters": ["zoom"]
      },
      "length": 3,
      "default": [0.0, 0.0, 1.0],
      "minimum": [0.0, 0.0, 0.0],
      "maximum": [1.0, 1.0, 1.0],
      "doc": "An array for configuring the fade-out effect for the front cutoff of content on pitched map views. It contains three values: start, range and final opacity. The start parameter defines the point at which the fade-out effect begins, with smaller values causing the effect to start earlier. The range parameter specifies how long the fade-out effect will last. A value of 0.0 for range makes content disappear immediately without a fade-out effect. The final opacity determines content opacity at the end of the fade-out effect. A value of 1.0 for final opacity means that the cutoff is completely disabled. Note: The property has no effect on the map if terrain or globe projection is enabled.",
      "sdk-support": {
        "basic functionality": {
          "js": "3.5.0"
        }
      }
    },
    "model-elevation-reference": {
      "type": "enum",
      "doc": "Selects the base of the model. Some modes might require precomputed elevation data in the tileset. When using vector tiled source as the model layer source and hd-road-markup elevation reference, this property acts as layout property and elevation is evaluated only in tile loading time.",
      "values": {
        "sea": {
          "doc": "Elevated rendering is enabled. Use this mode to elevate models relative to the sea level."
        },
        "ground": {
          "doc": "Elevated rendering is enabled. Use this mode to elevate models relative to the ground's height below them."
        },
        "hd-road-markup": {
          "doc": "Elevated rendering is enabled. Use this mode to describe additive and stackable features that should exist only on top of road polygons."
        }
      },
      "default": "ground",
      "transition": false,
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant",
      "sdk-support": {
        "basic functionality": {
          "js": "3.17.0",
          "android": "11.10.0",
          "ios": "11.10.0"
        }
      }
    },
    "model-line-cutout-mode": {
      "type": "enum",
      "doc": "Selects the way the line cutout effect is applied to the model.",
      "default": "enabled",
      "values": {
        "enabled": {
          "doc": "The model receives the cutout without any special handling."
        },
        "disabled": {
          "doc": "The cutout effect is disabled for the model."
        },
        "enabled-above-cutout": {
          "doc": "Only enabled if the model is above the cutout plane, such as for models above cut tunnels."
        }
      },
      "transition": false,
      "expression": {
        "interpolated": false
      },
      "property-type": "data-constant",
      "experimental": true,
      "private": true,
      "sdk-support": {
        "basic functionality": {
          "js": "3.26.0",
          "android": "11.26.0",
          "ios": "11.26.0"
        }
      }
    }
  },
  "transition": {
    "duration": {
      "type": "number",
      "default": 300,
      "minimum": 0,
      "units": "milliseconds",
      "doc": "Time allotted for transitions to complete."
    },
    "delay": {
      "type": "number",
      "default": 0,
      "minimum": 0,
      "units": "milliseconds",
      "doc": "Length of time before a transition begins."
    }
  },
  "property-type": {
    "data-driven": {
      "type": "property-type",
      "doc": "Property is interpolable and can be represented using a property expression."
    },
    "color-ramp": {
      "type": "property-type",
      "doc": "Property should be specified using a color ramp from which the output color can be sampled based on a property calculation."
    },
    "data-constant": {
      "type": "property-type",
      "doc": "Property is interpolable but cannot be represented using a property expression."
    },
    "constant": {
      "type": "property-type",
      "doc": "Property is constant across all zoom levels and property values."
    }
  },
  "promoteId": {
    "*": {
      "type": "*",
      "doc": "A feature property name to use as the ID, or an expression to evaluate as the ID for feature state. The expression can only be feature dependent if it is used."
    }
  },
  "$doc": {
    "transition": {
      "type": "transition",
      "doc": "Controls how a property transitions to a new value. Overrides the style's root `transition` for this property."
    },
    "use-theme": {
      "type": "enum",
      "doc": "Controls whether the associated color property is affected by the global color theme. When set to \"none\", the color theme is ignored for this property. When set to \"default\", the color theme is applied.",
      "values": {
        "default": {
          "doc": "The color theme is applied to this property (default behavior)."
        },
        "none": {
          "doc": "The color theme is ignored for this property."
        }
      },
      "default": "default"
    }
  }
}
